import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = "";  // пустая строка = тот же домен

const api = axios.create({ baseURL: API });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem("token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const STATUS_META = {
  unassigned:          { label: "Нераспределена",        color: "#f59e0b", bg: "#f59e0b18" },
  in_work:             { label: "В работе",               color: "#2563eb", bg: "#2563eb12" },
  awaiting_parts:      { label: "Ожидаем запчасти",        color: "#7c3aed", bg: "#7c3aed12" },
  parts_clarification: { label: "Уточнение запчастей",     color: "#b45309", bg: "#b4530918" },
  parts_ordered:       { label: "Запчасти заказаны",       color: "#0891b2", bg: "#0891b212" },
  approval:            { label: "Согласование",            color: "#dc2626", bg: "#dc262618" },
  approval_sent:       { label: "Отправлено на согласование", color: "#ca8a04", bg: "#ca8a0418" },
  approval_approved:   { label: "Согласовано",             color: "#16a34a", bg: "#16a34a18" },
  closed:              { label: "Закрыта",                 color: "#6b7280", bg: "#6b728014" },
  service_center:      { label: "Сервис центр",             color: "#2563eb", bg: "#2563eb12" },
};

const CLOSE_REASON_META = {
  not_approved: { label: "Не согласовано",              color: "#dc2626", bg: "#dc262618" },
  written_off:  { label: "На списание / нет запчастей", color: "#78716c", bg: "#78716c18" },
};

// Возвращает мета статуса с учётом причины закрытия (close_reason)
function getStatusMeta(ticket) {
  if (ticket.status === "closed" && ticket.close_reason && CLOSE_REASON_META[ticket.close_reason]) {
    return CLOSE_REASON_META[ticket.close_reason];
  }
  return STATUS_META[ticket.status] || STATUS_META.unassigned;
}

const H_COLORS = {
  created: "#9ca3af", assign: "#2563eb", "status-change": "#ef4444",
  comment: "#f59e0b", photo: "#7c3aed", parts: "#10b981",
  accepted: "#16a34a", reopen_new: "#0891b2", reopen_parts: "#0891b2",
  cash: "#16a34a", "superadmin-edit": "#9333ea",
};

const ROLE_LABELS = {
  admin: "Администратор",
  mechanic: "Механик",
  parts: "Менеджер запчастей",
};

// Превращает номера телефонов в тексте в кликабельные tel: ссылки
const PHONE_REGEX = /(\+7|8)[\s\-]?\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}/g;
function linkifyPhones(text) {
  if (!text) return text;
  const parts = [];
  let lastIndex = 0, key = 0, match;
  PHONE_REGEX.lastIndex = 0;
  while ((match = PHONE_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const raw = match[0];
    const digits = raw.replace(/\D/g, "");
    const normalized = "7" + digits.slice(1);
    parts.push(
      <a key={`tel-${key++}`} href={`tel:+${normalized}`} className="phone-link"
        onClick={e => e.stopPropagation()}>{raw}</a>
    );
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" })
    + " " + d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
};

const daysAgo = (iso) => {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (diff === 0) return "сегодня";
  if (diff === 1) return "1 день";
  if (diff < 5) return `${diff} дня`;
  return `${diff} дней`;
};

// ── CSS ───────────────────────────────────────────────────────────────────────
const css = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html, body { height: 100%; }
:root {
  --bg: #f5f5f7; --surface: #fff; --border: #e5e7eb; --border2: #d1d5db;
  --text: #111827; --muted: #6b7280; --muted2: #9ca3af;
  --red: #ef4444; --red-bg: #fef2f2;
  --blue: #2563eb; --blue-bg: #eff6ff;
  --radius: 10px;
}
[data-theme="dark"] {
  --bg: #0f1117; --surface: #1c1f26; --border: #2d3139; --border2: #3d4149;
  --text: #f0f2f5; --muted: #8b95a1; --muted2: #5c6370;
  --red: #f87171; --red-bg: #2d1a1a;
  --blue: #60a5fa; --blue-bg: #1a2535;
}
[data-theme="dark"] .search-input:focus { background: var(--surface); }
[data-theme="dark"] .form-input:focus, [data-theme="dark"] .form-select:focus, [data-theme="dark"] .form-textarea:focus { background: var(--surface); }
[data-theme="dark"] .overlay { background: rgba(0,0,0,0.65); }
[data-theme="dark"] .lightbox { background: rgba(0,0,0,0.92); }
[data-theme="dark"] .btn-outline { background: var(--surface); }
[data-theme="dark"] .desc-box { background: var(--bg); }
body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; background: var(--bg); color: var(--text); }
::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 4px; }
.app { min-height: 100vh; display: flex; flex-direction: column; }

/* login */
.login-screen { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: var(--bg); }
.login-box { background: var(--surface); border: 1.5px solid var(--border); border-radius: 14px; padding: 32px 28px; width: 100%; max-width: 360px; }
.login-logo { font-size: 20px; font-weight: 700; letter-spacing: -0.5px; margin-bottom: 4px; }
.login-logo .dot { color: var(--red); }
.login-sub { font-size: 13px; color: var(--muted); margin-bottom: 24px; }
.login-error { background: var(--red-bg); color: var(--red); font-size: 13px; padding: 8px 12px; border-radius: 7px; margin-bottom: 14px; }

/* topbar */
.topbar { background: var(--surface); border-bottom: 1px solid var(--border); min-height: 50px; padding: 8px 12px; display: flex; align-items: center; gap: 8px; position: sticky; top: 0; z-index: 100; flex-wrap: nowrap; overflow: hidden; }
.topbar-logo { font-size: 14px; font-weight: 700; letter-spacing: -0.3px; white-space: nowrap; flex-shrink: 0; }
.topbar-logo .dot { color: var(--red); }
.topbar-role { font-size: 10px; font-weight: 600; color: var(--blue); background: var(--blue-bg); padding: 3px 7px; border-radius: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px; }
.topbar-right { margin-left: auto; flex-shrink: 0; }
.topbar-switch { font-size: 12px; color: var(--muted); background: none; border: none; cursor: pointer; padding: 6px 8px; border-radius: 6px; transition: all 0.15s; font-family: inherit; white-space: nowrap; }
.topbar-switch:hover { background: var(--bg); color: var(--text); }

/* nav tabs */
.nav-tabs { background: var(--surface); border-bottom: 1px solid var(--border); display: flex; overflow-x: auto; padding: 0 12px; -webkit-overflow-scrolling: touch; flex-shrink: 0; }
.nav-tabs::-webkit-scrollbar { height: 0; }
.nav-tab { flex-shrink: 0; padding: 11px 13px; font-size: 13px; font-weight: 500; color: var(--muted); border: none; background: none; cursor: pointer; border-bottom: 2px solid transparent; transition: all 0.15s; display: flex; align-items: center; gap: 5px; font-family: inherit; white-space: nowrap; }
.nav-tab:hover { color: var(--text); }
.nav-tab.active { color: var(--blue); border-bottom-color: var(--blue); }
.nav-count { font-size: 10px; font-weight: 700; padding: 1px 5px; border-radius: 9px; min-width: 17px; text-align: center; }
.nav-count.red { background: var(--red); color: #fff; }
.nav-count.blue { background: var(--blue); color: #fff; }
.nav-count.muted { background: var(--border2); color: var(--muted); }

/* filter bar */
.filter-bar { background: var(--surface); border-bottom: 1px solid var(--border); padding: 10px 16px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.search-wrap { position: relative; flex: 1; min-width: 160px; }
.search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--muted2); font-size: 13px; pointer-events: none; }
.search-input { width: 100%; background: var(--bg); border: 1.5px solid var(--border); border-radius: 8px; padding: 7px 10px 7px 30px; font-size: 13px; font-family: inherit; outline: none; transition: border 0.15s; color: var(--text); }
.search-input:focus { border-color: var(--blue); background: #fff; }
.search-input::placeholder { color: var(--muted2); }
.search-clear { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--muted2); cursor: pointer; font-size: 14px; line-height: 1; padding: 2px; }
.filter-select { background: var(--bg); border: 1.5px solid var(--border); border-radius: 8px; padding: 7px 10px; font-size: 12px; font-family: inherit; color: var(--text); outline: none; cursor: pointer; transition: border 0.15s; flex-shrink: 0; }
.filter-select:focus { border-color: var(--blue); }
.filter-results { font-size: 12px; color: var(--muted); white-space: nowrap; }

/* content */
.content { flex: 1; padding: 14px 16px; max-width: 720px; margin: 0 auto; width: 100%; }
.page-hdr { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 8px; }
.page-hdr-title { font-size: 16px; font-weight: 700; }

/* buttons */
.btn { display: inline-flex; align-items: center; gap: 5px; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; font-family: inherit; white-space: nowrap; }
.btn-primary { background: var(--blue); color: #fff; }
.btn-primary:hover { background: #1d4ed8; }
.btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
.btn-outline { background: var(--surface); color: var(--text); border: 1.5px solid var(--border2); }
.btn-outline:hover { border-color: var(--blue); color: var(--blue); }
.btn-red { background: var(--red); color: #fff; }
.btn-red:hover { background: #dc2626; }
.btn-ghost { background: none; color: var(--muted); border: 1.5px solid var(--border); }
.btn-ghost:hover { color: var(--text); border-color: var(--border2); }
.btn-green { background: #10b981; color: #fff; }
.btn-green:hover { background: #059669; }
.btn-sm { padding: 5px 10px; font-size: 12px; }

/* empty / loading */
.empty { text-align: center; padding: 56px 20px; color: var(--muted); }
.empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.3; }
.empty-text { font-size: 13px; }

/* ticket list */
.ticket-list { display: flex; flex-direction: column; gap: 7px; }
.ticket-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 12px 14px; cursor: pointer; transition: all 0.15s; }
.ticket-card:hover { border-color: var(--blue); box-shadow: 0 2px 10px rgba(37,99,235,0.07); }
.ticket-card.transferred { border-left: 3px solid #f59e0b; }
.ticket-card.urgent { border-left: 4px solid var(--red); background: var(--red-bg); }
.ticket-card.urgent:hover { border-color: var(--red); box-shadow: 0 2px 10px rgba(239,68,68,0.15); }
.urgent-toggle { border: none; background: transparent; cursor: pointer; font-size: 15px; line-height: 1;
  padding: 2px 4px; border-radius: 6px; opacity: 0.3; filter: grayscale(1); transition: all 0.15s; }
.urgent-toggle:hover { opacity: 0.7; }
.urgent-toggle.active { opacity: 1; filter: none; }
.urgent-switch { display: flex; align-items: center; gap: 8px; padding: 9px 14px; border-radius: 10px;
  border: 1.5px solid var(--border); background: var(--surface); color: var(--muted); font-size: 14px;
  font-weight: 600; cursor: pointer; transition: all 0.15s; width: 100%; }
.urgent-switch-icon { font-size: 16px; filter: grayscale(1); opacity: 0.5; transition: all 0.15s; }
.urgent-switch.active { border-color: var(--red); background: var(--red-bg); color: var(--red); }
.urgent-switch.active .urgent-switch-icon { filter: none; opacity: 1; }
.cash-switch { display: flex; align-items: center; gap: 8px; padding: 9px 14px; border-radius: 10px;
  border: 1.5px solid var(--border); background: var(--surface); color: var(--muted); font-size: 14px;
  font-weight: 600; cursor: pointer; transition: all 0.15s; width: 100%; }
.cash-switch-icon { font-size: 16px; filter: grayscale(1); opacity: 0.5; transition: all 0.15s; }
.cash-switch.active { border-color: #16a34a; background: #f0fdf4; color: #16a34a; }
.cash-switch.active .cash-switch-icon { filter: none; opacity: 1; }
[data-theme="dark"] .cash-switch.active { background: rgba(22,163,74,0.12); }
.badge-accepted { font-size: 13px; line-height: 1; }
.accept-btn { border: 1.5px solid #16a34a; background: #f0fdf4; color: #16a34a; font-size: 11px; font-weight: 700;
  padding: 3px 8px; border-radius: 999px; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.accept-btn:hover { background: #16a34a; color: #fff; }
[data-theme="dark"] .accept-btn { background: rgba(22,163,74,0.12); }
[data-theme="dark"] .accept-btn:hover { background: #16a34a; color: #fff; }
.unassign-btn { border: 1.5px solid var(--border); background: var(--surface); color: var(--muted); font-size: 13px;
  line-height: 1; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s; padding: 0; }
.unassign-btn:hover { border-color: var(--blue); color: var(--blue); background: var(--blue-bg); }
.tc-top { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 7px; flex-wrap: wrap; }
.tc-id { font-size: 11px; color: var(--muted2); font-weight: 500; flex-shrink: 0; margin-top: 2px; }
.tc-text { font-size: 13px; font-weight: 500; flex: 1; min-width: 120px; line-height: 1.4; color: var(--text); word-break: break-word; }
.tc-badges { display: flex; gap: 5px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; max-width: 100%; }
.badge { font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 5px; white-space: nowrap; }
.badge-transfer { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
.tc-meta { display: flex; flex-wrap: wrap; gap: 10px; font-size: 11px; color: var(--muted); }
.tc-meta-item { display: flex; align-items: center; gap: 3px; }

/* modal */
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 200; display: flex; align-items: flex-end; justify-content: center; padding: 0; }
@media (min-width: 600px) { .overlay { align-items: center; padding: 20px; } }
.modal { background: var(--surface); border-radius: 16px 16px 0 0; width: 100%; max-width: 580px; max-height: 92vh; overflow-y: auto; padding: 18px 18px 28px; position: relative; }
@media (min-width: 600px) { .modal { border-radius: 14px; max-height: 88vh; } }
.modal-handle { width: 34px; height: 4px; background: var(--border2); border-radius: 2px; margin: 0 auto 14px; }
@media (min-width: 600px) { .modal-handle { display: none; } }
.modal-close { position: absolute; top: 14px; right: 14px; background: var(--bg); border: none; color: var(--muted); width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 13px; transition: all 0.15s; }
.modal-close:hover { background: var(--border); color: var(--text); }
.modal-title { font-size: 16px; font-weight: 700; margin-bottom: 14px; }

/* form */
.form-group { margin-bottom: 12px; }
.form-label { display: block; font-size: 11px; font-weight: 600; color: var(--muted); margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.4px; }
.form-input, .form-select, .form-textarea { width: 100%; background: var(--bg); border: 1.5px solid var(--border); color: var(--text); border-radius: 8px; padding: 9px 12px; font-size: 13px; font-family: inherit; outline: none; transition: border 0.15s; }
.form-input:focus, .form-select:focus, .form-textarea:focus { border-color: var(--blue); background: #fff; }
.form-textarea { resize: vertical; min-height: 100px; line-height: 1.5; }
.form-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }

/* divider */
.divider { border: none; border-top: 1px solid var(--border); margin: 14px 0; }

/* detail */
.kv-row { display: flex; gap: 6px; margin-bottom: 6px; font-size: 12px; }
.kv-row label { color: var(--muted); flex-shrink: 0; min-width: 70px; }
.kv-row value { color: var(--text); font-weight: 500; }
.desc-box { background: var(--bg); border-radius: 8px; padding: 12px; font-size: 13px; line-height: 1.55; margin-bottom: 12px; white-space: pre-wrap; word-break: break-word; }
.phone-link { color: var(--blue); text-decoration: underline; text-decoration-style: dotted; font-weight: 600; }
.phone-link:hover { text-decoration-style: solid; }

/* history */
.sec-label { font-size: 10px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.7px; margin-bottom: 8px; }
.history { display: flex; flex-direction: column; gap: 5px; }
.h-item { display: flex; gap: 8px; align-items: flex-start; font-size: 12px; padding: 7px 10px; border-radius: 7px; background: var(--bg); }
.h-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; margin-top: 3px; }
.h-text { flex: 1; color: var(--text); line-height: 1.4; }
.h-time { font-size: 11px; color: var(--muted2); white-space: nowrap; }

/* photos */
.photo-grid { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 7px; }
.photo-thumb { width: 72px; height: 72px; object-fit: cover; border-radius: 7px; cursor: zoom-in; border: 1.5px solid var(--border); transition: border-color 0.15s; }
.photo-thumb:hover { border-color: var(--blue); }
.photo-thumb-wrap { position: relative; width: 72px; height: 72px; }
.photo-thumb-wrap .photo-thumb { width: 100%; height: 100%; }
.photo-remove-btn { position: absolute; top: -6px; right: -6px; width: 20px; height: 20px; border-radius: 50%;
  background: var(--red); color: #fff; border: 2px solid var(--card-bg, #fff); font-size: 12px; line-height: 1;
  display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
.photo-remove-btn:hover { opacity: 0.85; }
.pdf-chip { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  font-size: 18px; background: var(--bg-soft, #f4f4f4); text-align: center; padding: 4px; cursor: default; }
.pdf-chip span { font-size: 8px; line-height: 1.1; color: var(--muted); word-break: break-all;
  max-width: 64px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

/* upload */
.upload-zone { border: 1.5px dashed var(--border2); border-radius: 8px; padding: 14px; text-align: center; cursor: pointer; transition: all 0.15s; color: var(--muted); font-size: 13px; }
.upload-zone:hover { border-color: var(--blue); color: var(--blue); background: var(--blue-bg); }
.upload-zone input { display: none; }

/* action panel */
.action-panel { background: var(--bg); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 12px; margin-top: 12px; }
.action-panel-label { font-size: 10px; font-weight: 700; color: var(--blue); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
.action-btns { display: flex; flex-wrap: wrap; gap: 7px; }
.assign-row { display: flex; gap: 7px; align-items: center; }
.assign-row .form-select { flex: 1; }

/* lightbox */
.lightbox { position: fixed; inset: 0; background: rgba(0,0,0,0.9); z-index: 400; display: flex; align-items: center; justify-content: center; cursor: zoom-out; padding: 20px; overflow: hidden; }
.lightbox img { max-width: 100%; max-height: 100%; border-radius: 8px; object-fit: contain; touch-action: none; user-select: none; }
.lightbox-close { position: fixed; top: 16px; right: 16px; width: 38px; height: 38px; border-radius: 50%;
  background: rgba(255,255,255,0.15); color: #fff; border: none; font-size: 18px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; z-index: 401; backdrop-filter: blur(4px); }
.lightbox-close:hover { background: rgba(255,255,255,0.28); }

/* users page */
.user-list { display: flex; flex-direction: column; gap: 7px; }
.user-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 12px 14px; display: flex; align-items: center; gap: 10px; }
.user-avatar { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
.user-info { flex: 1; min-width: 0; }
.user-name { font-size: 13px; font-weight: 600; color: var(--text); }
.user-login { font-size: 11px; color: var(--muted); margin-top: 1px; }
.user-role-badge { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 5px; white-space: nowrap; }
.user-actions { display: flex; gap: 6px; flex-shrink: 0; }
.btn-icon { background: none; border: 1.5px solid var(--border); border-radius: 7px; padding: 5px 8px; cursor: pointer; font-size: 13px; transition: all 0.15s; color: var(--muted); }
.btn-icon:hover { border-color: var(--blue); color: var(--blue); }
.btn-icon.danger:hover { border-color: var(--red); color: var(--red); }
.btn-icon:disabled { opacity: 0.3; cursor: not-allowed; }
.form-error { background: var(--red-bg); color: var(--red); font-size: 12px; padding: 7px 10px; border-radius: 7px; margin-bottom: 12px; }
.form-success { background: #f0fdf4; color: #15803d; font-size: 12px; padding: 7px 10px; border-radius: 7px; margin-bottom: 12px; }

.btn-icon { padding: 0 10px; font-size: 16px; background: var(--bg); border: 1.5px solid var(--border); border-radius: var(--radius); cursor: pointer; height: 38px; display: inline-flex; align-items: center; justify-content: center; transition: background 0.15s; }
.btn-icon:hover { background: var(--border); }
.btn-icon.copied { background: #dcfce7; border-color: #16a34a; }

.pdf-link { display: inline-flex; align-items: center; gap: 5px; background: #eff6ff; border: 1.5px solid #bfdbfe; border-radius: 7px; padding: 6px 10px; font-size: 12px; color: var(--blue); text-decoration: none; }
.pdf-link:hover { background: #dbeafe; }

/* parts comments on card */
.tc-parts-comments { margin-top: 7px; padding-top: 7px; border-top: 1px solid var(--border); }
.tc-parts-label { font-size: 11px; font-weight: 600; color: #7c3aed; display: block; margin-bottom: 4px; }
.tc-parts-comment { font-size: 12px; color: var(--text); line-height: 1.4; padding: 3px 0; border-bottom: 1px dashed var(--border); }
.tc-parts-comment:last-child { border-bottom: none; }

mark { background: #fef08a; color: inherit; border-radius: 2px; padding: 0 1px; }
`;

function Highlight({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return <>{text.slice(0, idx)}<mark>{text.slice(idx, idx + query.length)}</mark>{text.slice(idx + query.length)}</>;
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  });
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [tickets, setTickets] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [tab, setTab] = useState("unassigned");
  const [modal, setModal] = useState(null);
  const [sel, setSel] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [ticketsLoadedOnce, setTicketsLoadedOnce] = useState(false);
  const [ticketNotFoundMsg, setTicketNotFoundMsg] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "light" ? "dark" : "light");
  const [filterMechanic, setFilterMechanic] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const loadingRef = useRef(false);

  // Открытие заявки по клику на push-уведомление (?ticket=123 в адресе)
  useEffect(() => {
    if (!ticketsLoadedOnce) return;
    const params = new URLSearchParams(window.location.search);
    const ticketId = params.get("ticket");
    if (!ticketId) return;

    const found = tickets.find(t => String(t.id) === String(ticketId));
    if (found) {
      setSel(found);
      setModal("detail");
    } else {
      setTicketNotFoundMsg(true);
      setTimeout(() => setTicketNotFoundMsg(false), 4000);
    }
    // Убираем параметр из адреса, чтобы при обновлении страницы это не повторялось
    window.history.replaceState({}, "", window.location.pathname);
  }, [tickets, ticketsLoadedOnce]);

  const loadTickets = useCallback(async (silent = false) => {
    if (!user) return;
    if (loadingRef.current) return; // защита от накладывающихся запросов
    loadingRef.current = true;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/api/tickets");
      setTickets(data);
      setTicketsLoadedOnce(true);
      // Обновляем badge — не блокируем загрузку заявок
      (async () => {
        try {
          let badgeCount = 0;
          if (user.role === "mechanic") {
            badgeCount = data.filter(t => t.status === "in_work").length;
          } else if (["admin", "superadmin"].includes(user.role)) {
            badgeCount = data.filter(t => t.status === "unassigned").length;
          }
          if ("setAppBadge" in navigator) {
            if (badgeCount > 0) await navigator.setAppBadge(badgeCount);
            else await navigator.clearAppBadge();
          }
          if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: "set-badge", count: badgeCount });
          }
        } catch (e) { /* badge не критичен */ }
      })();
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally {
      if (!silent) setLoading(false);
      loadingRef.current = false;
    }
  }, [user]);

  const loadMechanics = useCallback(async () => {
    if (user?.role === "admin" || user?.role === "superadmin") {
      const r = await api.get("/api/mechanics");
      setMechanics(r.data);
    }
  }, [user]);

  // ── Push-уведомления ──────────────────────────────────────────────────────
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  };

  const setupPushSubscription = useCallback(async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (Notification.permission === "denied") return;

      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }
      if (permission !== "granted") return;

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { data } = await api.get("/api/push/vapid-public-key");
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }

      await api.post("/api/push/subscribe", sub.toJSON());
    } catch (e) {
      console.warn("Push subscription failed:", e.message);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadTickets();
      loadMechanics();
      setupPushSubscription();
      const interval = setInterval(() => loadTickets(true), 30000);
      return () => clearInterval(interval);
    }
  }, [user, loadTickets, loadMechanics, setupPushSubscription]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setTickets([]);
  };

  const onLogin = (u) => {
    setUser(u);
    setTab(u.role === "parts" ? "parts" : (u.role === "admin" || u.role === "superadmin") ? "unassigned" : "active");
  };

  const PARTS_STAGE = ["awaiting_parts", "parts_clarification", "parts_ordered"];
  const APPROVAL_STAGE = ["approval", "approval_sent", "approval_approved"];

  const tabsForRole = () => {
    if (!user) return [];
    if (user.role === "admin" || user.role === "superadmin") {
      const base = [
        { id: "unassigned", label: "Нераспределённые", accent: "red",
          count: tickets.filter(t => t.status === "unassigned").length },
        { id: "active",  label: "В работе",  accent: "blue",
          count: tickets.filter(t => t.status === "in_work").length },
        { id: "parts",   label: "Запчасти",  accent: "blue",
          count: tickets.filter(t => PARTS_STAGE.includes(t.status)).length },
        { id: "approval", label: "Согласование", accent: "red",
          count: tickets.filter(t => APPROVAL_STAGE.includes(t.status)).length },
        { id: "service_center", label: "🏢 Сервис центр", accent: "blue",
          count: tickets.filter(t => t.status === "service_center").length },
        { id: "closed",  label: "Закрытые заявки", accent: "muted",
          count: Math.min(tickets.filter(t => t.status === "closed").length, 300) },
        { id: "all",     label: "Все", accent: "muted", count: tickets.length },
        { id: "cash",    label: "💵 Наличка", accent: "muted", count: null },
        { id: "users",   label: "👥 Сотрудники", accent: "muted", count: null },
      ];
      if (user.role === "superadmin") base.push({ id: "reports", label: "📊 Отчёты", accent: "muted", count: null });
      return base;
    }
    if (user.role === "mechanic") {
      const PARTS_AND_APPROVAL = [...PARTS_STAGE, ...APPROVAL_STAGE];
      return [
        { id: "active", label: "Мои заявки", accent: "blue",
          count: tickets.filter(t => t.status === "in_work").length },
        { id: "parts",  label: "Ожидаем запчасти", accent: "blue",
          count: tickets.filter(t => PARTS_AND_APPROVAL.includes(t.status)).length },
        { id: "myclosed", label: "Мои закрытые заявки", accent: "muted",
          count: Math.min(tickets.filter(t => t.status === "closed").length, 50) },
        { id: "cash", label: "💵 Наличка", accent: "muted", count: null },
      ];
    }
    // parts role
    return [
      { id: "parts", label: "Запчасти", accent: "blue",
        count: tickets.filter(t => PARTS_STAGE.includes(t.status)).length },
      { id: "approval", label: "Согласование", accent: "red",
        count: tickets.filter(t => APPROVAL_STAGE.includes(t.status)).length },
      { id: "service_center", label: "🏢 Сервис центр", accent: "blue",
        count: tickets.filter(t => t.status === "service_center").length },
    ];
  };

  const baseList = () => {
    if (user?.role === "admin" || user?.role === "superadmin") {
      if (tab === "unassigned") return tickets.filter(t => t.status === "unassigned");
      if (tab === "active")     return tickets.filter(t => t.status === "in_work");
      if (tab === "parts")      return tickets.filter(t => PARTS_STAGE.includes(t.status));
      if (tab === "approval")   return tickets.filter(t => APPROVAL_STAGE.includes(t.status));
      if (tab === "service_center") return tickets.filter(t => t.status === "service_center");
      if (tab === "closed")     return tickets.filter(t => t.status === "closed").slice(0, 300);
      if (tab === "users")      return [];
      if (tab === "reports")    return [];
      if (tab === "cash")       return [];
      return tickets;
    }
    if (user?.role === "mechanic") {
      if (tab === "active") return tickets.filter(t => t.status === "in_work");
      if (tab === "cash") return [];
      if (tab === "myclosed") {
        return tickets
          .filter(t => t.status === "closed")
          .slice()
          .sort((a, b) => new Date(b.closed_at || b.created_at) - new Date(a.closed_at || a.created_at))
          .slice(0, 50);
      }
      const PARTS_AND_APPROVAL = [...PARTS_STAGE, ...APPROVAL_STAGE];
      return tickets.filter(t => PARTS_AND_APPROVAL.includes(t.status));
    }
    // parts role
    if (tab === "approval") return tickets.filter(t => APPROVAL_STAGE.includes(t.status));
    if (tab === "service_center") return tickets.filter(t => t.status === "service_center");
    return tickets.filter(t => PARTS_STAGE.includes(t.status));
  };


  const filteredList = () => {
    let list = baseList();
    if (filterMechanic !== "all") list = list.filter(t => String(t.assigned_to) === filterMechanic);
    if (filterStatus !== "all") list = list.filter(t => t.status === filterStatus);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t =>
        `#${t.id}`.includes(q) ||
        (t.text || "").toLowerCase().includes(q) ||
        (t.assigned_name || "").toLowerCase().includes(q) ||
        (t.manager_comment || "").toLowerCase().includes(q) ||
        (t.approval_comment || "").toLowerCase().includes(q) ||
        (t.parts_comments || []).some(c => (c.text || "").toLowerCase().includes(q))
      );
    }
    // Срочные заявки — наверх списка (только в "Нераспределённые" и "В работе")
    const urgentFirstTabs = (user?.role === "admin" || user?.role === "superadmin")
      ? ["unassigned", "active"]
      : user?.role === "mechanic" ? ["active"] : [];
    if (urgentFirstTabs.includes(tab)) {
      list = [...list].sort((a, b) => (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0));
    }
    return list;
  };

  const createTicket = async (text, files, urgent) => {
    const result = await api.post("/api/tickets", { text, urgent: !!urgent });
    const ticketId = result.data.id;
    if (files?.length && ticketId) {
      const fd = new FormData();
      files.forEach(f => fd.append("photos", f));
      fd.append("photoType", "ticket");
      await api.post(`/api/tickets/${ticketId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    }
    setModal(null);
    loadTickets();
  };

  // Механик создаёт заявку прямо из своего кабинета — сразу в "Сервис центр"
  const createServiceCenterTicket = async (text, files, urgent) => {
    const result = await api.post("/api/tickets/service-center", { text, urgent: !!urgent });
    const ticketId = result.data.id;
    if (files?.length && ticketId) {
      const fd = new FormData();
      files.forEach(f => fd.append("photos", f));
      fd.append("photoType", "ticket");
      await api.post(`/api/tickets/${ticketId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    }
    setModal(null);
    loadTickets();
  };

  const assignTicket = async (id, mechanic_id) => {
    await api.patch(`/api/tickets/${id}/assign`, { mechanic_id });
    loadTickets();
  };

  const unassignTicket = async (id) => {
    if (!window.confirm("Вернуть заявку в нераспределённые? Назначение механику будет снято.")) return;
    await api.patch(`/api/tickets/${id}/unassign`);
    loadTickets();
  };

  const sendToParts = async (id) => {
    if (!window.confirm("Отправить заявку сразу менеджеру запчастей, минуя механика?")) return;
    await api.patch(`/api/tickets/${id}/to-parts`);
    loadTickets();
  };

  const editTicket = async (id, text) => {
    await api.patch(`/api/tickets/${id}`, { text });
    loadTickets();
  };

  const deleteTicket = async (id) => {
    await api.delete(`/api/tickets/${id}`);
    loadTickets();
  };

  const toggleUrgent = async (id, urgent) => {
    // Оптимистичное обновление — сразу отражаем в интерфейсе, не дожидаясь ответа сервера
    setTickets(prev => prev.map(t => t.id === id ? { ...t, urgent } : t));
    try {
      await api.patch(`/api/tickets/${id}/urgent`, { urgent });
    } catch (e) {
      // Откат при ошибке
      setTickets(prev => prev.map(t => t.id === id ? { ...t, urgent: !urgent } : t));
    }
  };

  const acceptTicket = async (id) => {
    const prevVal = tickets.find(t => t.id === id)?.accepted_at;
    setTickets(prev => prev.map(t => t.id === id ? { ...t, accepted_at: new Date().toISOString() } : t));
    try {
      await api.patch(`/api/tickets/${id}/accept`);
    } catch (e) {
      setTickets(prev => prev.map(t => t.id === id ? { ...t, accepted_at: prevVal } : t));
      alert(e.response?.data?.error || "Не удалось принять заявку");
    }
  };

  const reopenTicket = async (id, mode, comment) => {
    await api.patch(`/api/tickets/${id}/reopen`, { mode, comment });
    loadTickets();
  };

  const softDeleteTicket = async (id, reason) => {
    await api.patch(`/api/tickets/${id}/soft-delete`, { reason });
    loadTickets();
  };

  const mechanicAction = async (id, action, comment, files, photoType, cashData) => {
    // Сначала загружаем фото — статус меняем только после подтверждённой загрузки.
    // Это защищает от ситуации с плохим интернетом: если фото не загрузилось,
    // заявка НЕ должна улетать в "закрыта"/"ожидаем запчасти" без фото.
    if (files?.length) {
      const fd = new FormData();
      files.forEach(f => fd.append("photos", f));
      fd.append("photoType", photoType || "parts");
      await api.post(`/api/tickets/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    }
    await api.patch(`/api/tickets/${id}/action`, {
      action, comment,
      is_cash: cashData?.isCash || false,
      cash_amount: cashData?.amount || null,
    });
    loadTickets();
  };

  const partsReady = async (id) => {
    await api.patch(`/api/tickets/${id}/parts-ready`);
    loadTickets();
  };

  const partsAction = async (id, action) => {
    await api.patch(`/api/tickets/${id}/parts-action`, { action });
    loadTickets();
  };

  const approvalAction = async (id, action) => {
    await api.patch(`/api/tickets/${id}/approval-action`, { action });
    loadTickets();
  };

  const saveManagerComment = async (id, comment) => {
    await api.patch(`/api/tickets/${id}/manager-comment`, { comment });
    loadTickets();
  };

  const saveApprovalComment = async (id, comment) => {
    await api.patch(`/api/tickets/${id}/approval-comment`, { comment });
    loadTickets();
  };

  if (!user) return (
    <>
      <style>{css}</style>
      <LoginScreen onLogin={onLogin} />
    </>
  );

  const tabs = tabsForRole();
  const list = filteredList();
  const showMechanicFilter = (user.role === "admin" || user.role === "superadmin") && (tab === "active" || tab === "all" || tab === "closed");
  const showStatusFilter = (tab === "parts" || tab === "approval") &&
    (user.role === "parts" || user.role === "admin" || user.role === "superadmin");
  const STATUS_FILTER_OPTIONS = {
    parts: [
      ["awaiting_parts", "Ожидаем запчасти"],
      ["parts_clarification", "Уточнение запчастей"],
      ["parts_ordered", "Запчасти заказаны"],
    ],
    approval: [
      ["approval", "Согласование"],
      ["approval_sent", "Отправлено на согласование"],
      ["approval_approved", "Согласовано"],
    ],
  };
  const roleLabel = user.role === "superadmin" ? "Супер Админ"
    : user.role === "admin" ? "Администратор"
    : user.role === "parts" ? "Менеджер запчастей"
    : `Механик · ${user.name?.split(" ")[1] || user.name}`;

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <div className="topbar">
          <div className="topbar-logo">Сервис<span className="dot">.</span>Контроль</div>
          <div className="topbar-role">{roleLabel}</div>
          <div className="topbar-right">
            <button className="topbar-switch" onClick={toggleTheme} title="Сменить тему" style={{ marginRight: 4 }}>
              {theme === "light" ? "🌙" : "☀️"}
            </button>
            <button className="topbar-switch" onClick={logout}>Выйти ↗</button>
          </div>
        </div>

        <div className="nav-tabs">
          {tabs.map(t => (
            <button key={t.id} className={`nav-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => { setTab(t.id); setSearch(""); setFilterMechanic("all"); setFilterStatus("all"); }}>
              {t.label}
              {t.count > 0 && <span className={`nav-count ${t.accent}`}>{t.count}</span>}
            </button>
          ))}
        </div>

        {tab === "users" ? (
          <UsersPage currentUserId={user.id} onUsersChange={loadMechanics} />
        ) : tab === "reports" ? (
          <ReportsPage tickets={tickets} mechanics={mechanics} user={user} />
        ) : tab === "cash" ? (
          <CashPage mechanics={mechanics} user={user} />
        ) : (
          <>
            <div className="filter-bar">
              <div className="search-wrap">
                <span className="search-icon">🔍</span>
                <input className="search-input" placeholder="Поиск по тексту, номеру, механику..."
                  value={search} onChange={e => setSearch(e.target.value)} />
                {search && <button className="search-clear" onClick={() => setSearch("")}>✕</button>}
              </div>
              {showMechanicFilter && (
                <select className="filter-select" value={filterMechanic} onChange={e => setFilterMechanic(e.target.value)}>
                  <option value="all">Все механики</option>
                  {mechanics.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                </select>
              )}
              {showStatusFilter && (
                <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="all">Все статусы</option>
                  {STATUS_FILTER_OPTIONS[tab].map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              )}
              {(search || filterMechanic !== "all" || filterStatus !== "all") && (
                <span className="filter-results">{list.length} зап.</span>
              )}
            </div>

            <div className="content">
              <div className="page-hdr">
                <div className="page-hdr-title">{tabs.find(t => t.id === tab)?.label}</div>
                {(user.role === "admin" || user.role === "superadmin") && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => setModal("maintenance")}>🔧 +ТО</button>
                    <button className="btn btn-primary btn-sm" onClick={() => setModal("create")}>+ Заявка</button>
                  </div>
                )}
                {user.role === "mechanic" && (
                  <button className="btn btn-primary btn-sm" onClick={() => setModal("create-sc")}>
                    🏢 + Заявка в Сервис центр
                  </button>
                )}
              </div>

              {loading ? (
                <div className="empty"><div className="empty-icon">⏳</div><div className="empty-text">Загрузка...</div></div>
              ) : list.length === 0 ? (
                <div className="empty">
                  <div className="empty-icon">{search ? "🔍" : "📭"}</div>
                  <div className="empty-text">{search ? "Ничего не найдено" : "Заявок нет"}</div>
                </div>
              ) : (
                <div className="ticket-list">
                  {list.map(t => (
                    <TicketCard key={t.id} ticket={t} search={search} userRole={user.role}
                      currentUserId={user.id}
                      onClick={() => { setSel(t); setModal("detail"); }}
                      onToggleUrgent={toggleUrgent}
                      onAccept={acceptTicket}
                      onUnassign={unassignTicket} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {modal === "create" && <CreateModal onClose={() => setModal(null)} onCreate={createTicket} />}
        {modal === "create-sc" && <CreateModal onClose={() => setModal(null)} onCreate={createServiceCenterTicket} />}
        {modal === "maintenance" && <MaintenanceModal onClose={() => setModal(null)} />}
        {modal === "detail" && sel && (
          <DetailModal
            ticket={tickets.find(t => t.id === sel.id) || sel}
            user={user}
            mechanics={mechanics}
            onClose={() => { setModal(null); setSel(null); }}
            onAssign={assignTicket}
            onUnassign={unassignTicket}
            onEdit={editTicket}
            onDelete={deleteTicket}
            onToggleUrgent={toggleUrgent}
            onAccept={acceptTicket}
            onReopen={reopenTicket}
            onSoftDelete={softDeleteTicket}
            onSendToParts={sendToParts}
            onMechanicAction={mechanicAction}
            onPartsReady={partsReady}
            onPartsAction={partsAction}
            onApprovalAction={approvalAction}
            onManagerComment={saveManagerComment}
            onApprovalComment={saveApprovalComment}
            onRefresh={loadTickets}
            onLightbox={src => setLightbox(src)}
          />
        )}
        {lightbox && <Lightbox key={lightbox} src={lightbox} onClose={() => setLightbox(null)} />}

        {ticketNotFoundMsg && (
          <div style={{
            position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 500,
            background: "var(--surface)", color: "var(--text)", border: "1.5px solid var(--red)",
            borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          }}>
            ⚠ Заявка недоступна
          </div>
        )}
      </div>
    </>
  );
}

// ── Users Page ────────────────────────────────────────────────────────────────
const ROLE_COLORS = {
  admin:    { bg: "#eff6ff", color: "#2563eb" },
  mechanic: { bg: "#f0fdf4", color: "#15803d" },
  parts:    { bg: "#faf5ff", color: "#7c3aed" },
};
const ROLE_AVATARS = { admin: "👑", mechanic: "🔧", parts: "📦" };

function UsersPage({ currentUserId, onUsersChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // "add" | { type: "password", user }
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/users");
      setUsers(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteUser = async (u) => {
    if (!window.confirm(`Удалить сотрудника «${u.name}»?\nЕго активные заявки вернутся в нераспределённые.`)) return;
    try {
      await api.delete(`/api/users/${u.id}`);
      await load();
      onUsersChange();
    } catch(e) {
      setError(e.response?.data?.error || "Ошибка удаления");
    }
  };

  return (
    <div className="content">
      <div className="page-hdr">
        <div className="page-hdr-title">Сотрудники</div>
        <button className="btn btn-primary btn-sm" onClick={() => setModal("add")}>+ Добавить</button>
      </div>
      {error && <div className="form-error">{error} <button style={{float:"right",background:"none",border:"none",cursor:"pointer",color:"inherit"}} onClick={() => setError("")}>✕</button></div>}
      {loading ? (
        <div className="empty"><div className="empty-icon">⏳</div><div className="empty-text">Загрузка...</div></div>
      ) : (
        <div className="user-list">
          {users.map(u => {
            const rc = ROLE_COLORS[u.role] || ROLE_COLORS.mechanic;
            const isMe = u.id === currentUserId;
            return (
              <div key={u.id} className="user-card">
                <div className="user-avatar" style={{ background: rc.bg, color: rc.color }}>
                  {ROLE_AVATARS[u.role] || "👤"}
                </div>
                <div className="user-info">
                  <div className="user-name">{u.name} {isMe && <span style={{fontSize:10,color:"var(--muted)"}}>— это вы</span>}</div>
                  <div className="user-login">@{u.login}</div>
                </div>
                <span className="user-role-badge" style={{ background: rc.bg, color: rc.color }}>
                  {ROLE_LABELS[u.role]}
                </span>
                <div className="user-actions">
                  <button className="btn-icon" title="Сменить пароль"
                    onClick={() => setModal({ type: "password", user: u })}>🔑</button>
                  <button className="btn-icon danger" title="Удалить" disabled={isMe}
                    onClick={() => deleteUser(u)}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal === "add" && (
        <AddUserModal
          onClose={() => setModal(null)}
          onCreated={() => { load(); onUsersChange(); }}
        />
      )}
      {modal?.type === "password" && (
        <ChangePasswordModal
          user={modal.user}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ── Add User Modal ────────────────────────────────────────────────────────────
function AddUserModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("mechanic");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!name.trim() || !login.trim() || !password.trim()) {
      setError("Заполните все поля"); return;
    }
    setLoading(true); setError("");
    try {
      await api.post("/api/users", { name: name.trim(), login: login.trim(), password, role });
      onCreated();
      onClose();
    } catch(e) {
      setError(e.response?.data?.error || "Ошибка создания");
    } finally { setLoading(false); }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">Новый сотрудник</div>
        {error && <div className="form-error">{error}</div>}
        <div className="form-group">
          <label className="form-label">Полное имя</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)}
            placeholder="Иван Иванов" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Логин</label>
          <input className="form-input" value={login} onChange={e => setLogin(e.target.value)}
            placeholder="ivanov" />
        </div>
        <div className="form-group">
          <label className="form-label">Пароль</label>
          <input className="form-input" type="password" value={password}
            onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <div className="form-group">
          <label className="form-label">Роль</label>
          <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
            <option value="mechanic">Механик</option>
            <option value="admin">Администратор</option>
            <option value="parts">Менеджер запчастей</option>
          </select>
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" disabled={loading} onClick={submit}>
            {loading ? "Создаём..." : "Создать"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Change Password Modal ─────────────────────────────────────────────────────
function ChangePasswordModal({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (password.length < 4) { setError("Минимум 4 символа"); return; }
    setLoading(true); setError("");
    try {
      await api.patch(`/api/users/${user.id}/password`, { password });
      setSuccess(true);
      setTimeout(onClose, 1200);
    } catch(e) {
      setError(e.response?.data?.error || "Ошибка");
    } finally { setLoading(false); }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">Смена пароля</div>
        <div style={{fontSize:13,color:"var(--muted)",marginBottom:14}}>Сотрудник: <b>{user.name}</b></div>
        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">✓ Пароль изменён</div>}
        <div className="form-group">
          <label className="form-label">Новый пароль</label>
          <input className="form-input" type="password" value={password} autoFocus
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submit()}
            placeholder="Минимум 4 символа" />
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-primary" disabled={loading || success} onClick={submit}>
            {loading ? "Сохраняем..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!login || !password) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/api/login", { login, password });
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      onLogin(data.user);
    } catch (e) {
      setError(e.response?.data?.error || "Ошибка входа");
    } finally { setLoading(false); }
  };

  return (
    <div className="login-screen">
      <div className="login-box">
        <div className="login-logo">Сервис<span className="dot">.</span>Контроль</div>
        <div className="login-sub">Управление заявками на ремонт оборудования</div>
        {error && <div className="login-error">{error}</div>}
        <div className="form-group">
          <label className="form-label">Логин</label>
          <input className="form-input" value={login} onChange={e => setLogin(e.target.value)}
            placeholder="admin" onKeyDown={e => e.key === "Enter" && submit()} />
        </div>
        <div className="form-group">
          <label className="form-label">Пароль</label>
          <input className="form-input" type="password" value={password}
            onChange={e => setPassword(e.target.value)} placeholder="••••••••"
            onKeyDown={e => e.key === "Enter" && submit()} />
        </div>
        <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }}
          disabled={loading || !login || !password} onClick={submit}>
          {loading ? "Вход..." : "Войти"}
        </button>
      </div>
    </div>
  );
}

// ── Copy Button ───────────────────────────────────────────────────────────────
function CopyButton({ text, small }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { alert("Нет доступа к буферу обмена"); }
  };
  return (
    <button className={`btn-icon${copied ? " copied" : ""}`}
      style={{ height: small ? 24 : 30, fontSize: small ? 12 : 14, padding: "0 7px" }}
      title="Копировать" onClick={handleCopy}>
      {copied ? "✓" : "📋"}
    </button>
  );
}

// ── Ticket Card ───────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const lastDist = useRef(0);
  const dragging = useRef(false);

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      lastDist.current = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    } else if (pointers.current.size === 1 && scale > 1) {
      dragging.current = true;
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const prev = pointers.current.get(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (lastDist.current > 0) {
        const delta = dist / lastDist.current;
        setScale(s => clamp(s * delta, 1, 5));
      }
      lastDist.current = dist;
    } else if (pointers.current.size === 1 && dragging.current && scale > 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      setPos(p => ({ x: p.x + dx, y: p.y + dy }));
    }
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) lastDist.current = 0;
    if (pointers.current.size === 0) dragging.current = false;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setScale(s => {
      const next = clamp(s + delta * s, 1, 5);
      if (next <= 1) setPos({ x: 0, y: 0 });
      return next;
    });
  };

  const toggleZoom = () => {
    if (scale > 1) { setScale(1); setPos({ x: 0, y: 0 }); }
    else { setScale(2.5); }
  };

  return (
    <div className="lightbox" onClick={e => { if (e.target === e.currentTarget && scale === 1) onClose(); }}>
      <button className="lightbox-close" onClick={onClose}>✕</button>
      <img
        src={src}
        alt=""
        draggable={false}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={onWheel}
        onDoubleClick={toggleZoom}
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transition: pointers.current.size ? "none" : "transform 0.15s ease-out",
          cursor: scale > 1 ? "grab" : "zoom-in",
          touchAction: "none",
        }}
      />
    </div>
  );
}

function TicketCard({ ticket, search, onClick, userRole, currentUserId, onToggleUrgent, onAccept, onUnassign }) {
  const sm = getStatusMeta(ticket);
  const isTransfer = ticket.transferred && ticket.status === "unassigned";
  const isUrgent = !!ticket.urgent;
  const preview = (ticket.text || "").slice(0, 120) + ((ticket.text || "").length > 120 ? "…" : "");
  const partsComments = ticket.parts_comments || [];
  const isMechanicView = userRole === "mechanic";
  const canToggleUrgent = userRole === "admin" || userRole === "superadmin";
  const canUnassign = (userRole === "admin" || userRole === "superadmin") &&
    (ticket.status === "in_work" || ticket.status === "service_center");
  const isOwnMechanic = isMechanicView && ticket.assigned_to === currentUserId;
  const showAccept = ticket.status === "in_work";
  const isAccepted = !!ticket.accepted_at;

  const APPROVAL_BORDER = {
    approval: "#dc2626", approval_sent: "#ca8a04", approval_approved: "#16a34a",
  };
  const approvalBorder = APPROVAL_BORDER[ticket.status];

  return (
    <div className={`ticket-card ${isTransfer ? "transferred" : ""} ${isUrgent ? "urgent" : ""}`} onClick={onClick}
      style={!isUrgent && approvalBorder ? { borderLeft: `4px solid ${approvalBorder}`, background: approvalBorder + "0a" } : undefined}>
      <div className="tc-top">
        <span className="tc-id">#{ticket.id}</span>
        <span className="tc-text"><Highlight text={preview} query={search} /></span>
        <div className="tc-badges">
          {canToggleUrgent && (
            <button type="button" className={`urgent-toggle ${isUrgent ? "active" : ""}`}
              title={isUrgent ? "Снять пометку «Срочно»" : "Отметить как «Срочно»"}
              onClick={e => { e.stopPropagation(); onToggleUrgent(ticket.id, !isUrgent); }}>❗</button>
          )}
          {isTransfer && <span className="badge badge-transfer">Перенос</span>}
          {ticket.reopened && <span className="badge" style={{ background: "#cffafe", color: "#0891b2" }}>♻ Переоткрыта</span>}
          {ticket.cash_total > 0 && <span className="badge" style={{ background: "#dcfce7", color: "#16a34a" }}>💵 {ticket.cash_total}₽</span>}
          {ticket.sent_to_service_center && <span className="badge" style={{ background: "#dbeafe", color: "#2563eb" }}>🏢 Сервис центр</span>}
          {ticket.is_maintenance && <span className="badge" style={{ background: "#fef9c3", color: "#a16207" }}>🔧 ТО</span>}
          <span className="badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
          {showAccept && isAccepted && (
            <span className="badge-accepted" title="Механик принял заявку в работу">✅</span>
          )}
          {showAccept && !isAccepted && isOwnMechanic && (
            <button type="button" className="accept-btn"
              onClick={e => { e.stopPropagation(); onAccept(ticket.id); }}>✅ Принять</button>
          )}
          {canUnassign && (
            <button type="button" className="unassign-btn" title="Вернуть в нераспределённые"
              onClick={e => { e.stopPropagation(); onUnassign(ticket.id); }}>↩</button>
          )}
        </div>
      </div>
      <div className="tc-meta">
        {ticket.assigned_name
          ? <span className="tc-meta-item">👷 <Highlight text={ticket.assigned_name} query={search} /></span>
          : <span className="tc-meta-item" style={{ color: "var(--muted2)" }}>Не назначен</span>}
        <span className="tc-meta-item">🕐 {fmt(ticket.created_at)}</span>
        {ticket.status !== "closed" && (() => {
          const days = Math.floor((Date.now() - new Date(ticket.created_at).getTime()) / 86400000);
          const color = days >= 7 ? "#ef4444" : days >= 4 ? "#f59e0b" : "#10b981";
          return <span className="tc-meta-item" style={{ color, fontWeight: 600 }}>⏱ {daysAgo(ticket.created_at)}</span>;
        })()}
        {ticket.photos?.length > 0 && <span className="tc-meta-item">📷 {ticket.photos.length}</span>}
      </div>
      {partsComments.length > 0 && (
        <div className="tc-parts-comments">
          <span className="tc-parts-label">📦 К запчастям:</span>
          {partsComments.map((c, i) => (
            <div key={i} className="tc-parts-comment">
              <Highlight text={c.text} query={search} />
            </div>
          ))}
        </div>
      )}
      {!isMechanicView && ticket.manager_comment && (
        <div className="tc-parts-comments" style={{ borderTopColor: "#0891b2" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span className="tc-parts-label" style={{ color: "#0891b2" }}>🛠 Менеджер:</span>
            <CopyButton text={ticket.manager_comment} small />
          </div>
          <div className="tc-parts-comment"><Highlight text={ticket.manager_comment} query={search} /></div>
        </div>
      )}
      {!isMechanicView && ticket.approval_comment && (
        <div className="tc-parts-comments" style={{ borderTopColor: "#dc2626" }}>
          <span className="tc-parts-label" style={{ color: "#dc2626" }}>📝 Согласование:</span>
          <div className="tc-parts-comment"><Highlight text={ticket.approval_comment} query={search} /></div>
        </div>
      )}
    </div>
  );
}

// ── Create Modal ──────────────────────────────────────────────────────────────
function CreateModal({ onClose, onCreate }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [urgent, setUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const valid = text.trim().length > 0;

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setFiles(prev => [...prev, ...arr]);
    setPreviews(prev => [...prev, ...arr.map(f => f.type.startsWith("image/") ? URL.createObjectURL(f) : null)]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!valid) return;
    setLoading(true);
    await onCreate(text.trim(), files, urgent);
    setLoading(false);
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">Новая заявка</div>
        <div className="form-group">
          <label className="form-label">Текст заявки</label>
          <textarea className="form-textarea" style={{ minHeight: 140 }} value={text}
            onChange={e => setText(e.target.value)} autoFocus
            placeholder="Опишите задачу, адрес, оборудование, проблему..." />
        </div>
        <div className="form-group">
          <button type="button" className={`urgent-switch ${urgent ? "active" : ""}`}
            onClick={() => setUrgent(u => !u)}>
            <span className="urgent-switch-icon">❗</span>
            <span>Срочная заявка</span>
          </button>
        </div>
        <div className="form-group">
          <label className="form-label">Фото / PDF к заявке <span style={{fontWeight:400,textTransform:"none"}}>(необязательно)</span></label>
          <div className="upload-zone" onClick={() => fileRef.current?.click()}>
            <input ref={fileRef} type="file" accept="image/*,.pdf" multiple
              onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} />
            📎 Прикрепить фото или PDF
          </div>
          {files.length > 0 && (
            <div className="photo-grid" style={{ marginTop: 7 }}>
              {files.map((f, i) => (
                <div key={i} className="photo-thumb-wrap">
                  {previews[i]
                    ? <img className="photo-thumb" src={previews[i]} alt="" />
                    : <div className="photo-thumb pdf-chip" title={f.name}>📄<span>{f.name}</span></div>
                  }
                  <button type="button" className="photo-remove-btn" title="Удалить файл"
                    onClick={() => removeFile(i)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          <button className="btn btn-icon" title="Вставить из буфера обмена"
            onClick={async () => {
              try {
                const t = await navigator.clipboard.readText();
                if (t) setText(prev => prev ? prev + "\n" + t : t);
              } catch { alert("Нет доступа к буферу обмена"); }
            }}>📋</button>
          <button className="btn btn-primary" disabled={!valid || loading} onClick={submit}>
            {loading ? "Создаём..." : "Создать"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
function MaintenanceModal({ onClose }) {
  const [schedules, setSchedules] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [text, setText] = useState("");
  const [firstDate, setFirstDate] = useState(new Date().toISOString().slice(0, 10));
  const [interval, setInterval_] = useState(1);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoadingList(true);
    try {
      const res = await api.get("/api/maintenance-schedules");
      setSchedules(res.data || []);
    } catch (e) {
      // тихо игнорируем, список просто останется пустым
    }
    setLoadingList(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!text.trim()) { setError("Укажите текст заявки"); return; }
    if (!firstDate) { setError("Укажите первую дату"); return; }
    setError("");
    setSaving(true);
    try {
      await api.post("/api/maintenance-schedules", {
        text: text.trim(), first_date: firstDate, interval_months: Number(interval),
      });
      setText(""); setFirstDate(new Date().toISOString().slice(0, 10)); setInterval_(1);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось сохранить расписание");
    }
    setSaving(false);
  };

  const toggleActive = async (sch) => {
    setSchedules(prev => prev.map(s => s.id === sch.id ? { ...s, active: !s.active } : s));
    try {
      await api.patch(`/api/maintenance-schedules/${sch.id}`, { active: !sch.active });
    } catch (e) {
      load();
    }
  };

  const removeSchedule = async (id) => {
    if (!window.confirm("Удалить расписание ТО? Уже созданные заявки останутся.")) return;
    setSchedules(prev => prev.filter(s => s.id !== id));
    try {
      await api.delete(`/api/maintenance-schedules/${id}`);
    } catch (e) {
      load();
    }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">🔧 Расписания ТО</div>
        <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: -6, marginBottom: 14, lineHeight: 1.5 }}>
          Заявка появится в «Нераспределённых» автоматически, когда наступит указанная дата, и будет повторяться с заданной периодичностью.
        </p>

        <div className="action-panel">
          <div className="action-panel-label">Новое расписание</div>
          <div className="form-group">
            <label className="form-label">Текст заявки</label>
            <textarea className="form-textarea" style={{ minHeight: 70 }} value={text}
              onChange={e => { setText(e.target.value); setError(""); }}
              placeholder="Например: плановое ТО конвектомата, кафе «Ромашка»..." />
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
              <label className="form-label">Первая дата</label>
              <input type="date" className="form-input" value={firstDate}
                onChange={e => { setFirstDate(e.target.value); setError(""); }} />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
              <label className="form-label">Периодичность</label>
              <select className="form-select" value={interval} onChange={e => setInterval_(e.target.value)}>
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>Каждые {n} {n === 1 ? "месяц" : n < 5 ? "месяца" : "месяцев"}</option>
                ))}
              </select>
            </div>
          </div>
          {error && <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 8 }}>⚠ {error}</div>}
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={submit}>
            {saving ? "Сохранение..." : "+ Добавить расписание"}
          </button>
        </div>

        <hr className="divider" />

        <div className="sec-label">Существующие расписания ({schedules.length})</div>
        {loadingList ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>Загрузка...</div>
        ) : schedules.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>Расписаний пока нет</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {schedules.map(sch => (
              <div key={sch.id} style={{
                background: "var(--bg)", borderRadius: 8, padding: "10px 12px",
                opacity: sch.active ? 1 : 0.5,
              }}>
                <div style={{ fontSize: 13, marginBottom: 4 }}>{sch.text}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 11.5, color: "var(--muted)" }}>
                  <span>📅 Следующая: {sch.next_date?.slice(0, 10)}</span>
                  <span>🔁 Каждые {sch.interval_months} мес.</span>
                  <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" style={{ padding: "3px 8px", fontSize: 11.5 }}
                      onClick={() => toggleActive(sch)}>
                      {sch.active ? "⏸ Приостановить" : "▶ Возобновить"}
                    </button>
                    <button className="btn-icon danger" title="Удалить расписание"
                      onClick={() => removeSchedule(sch.id)}>🗑</button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PHOTO_TYPE_LABELS = { ticket: "📷 Заявка", parts: "📦 Запчасти", act: "📋 АВР", service_center: "🏢 Сервис центр" };

function SuperEditModal({ ticket, onClose, onRefresh }) {
  const photoBaseUrl = window.location.origin;
  const [text, setText] = useState(ticket.text || "");
  const [status, setStatus] = useState(ticket.status);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const fileRefs = { ticket: useRef(), parts: useRef(), act: useRef(), service_center: useRef() };

  const photosByType = (type) => (ticket.photos_typed || []).filter(p => p.type === type);

  const save = async () => {
    if (!text.trim()) { setError("Текст не может быть пустым"); return; }
    setError("");
    setSaving(true);
    try {
      await api.patch(`/api/tickets/${ticket.id}/superadmin-edit`, { text: text.trim(), status, comment: comment.trim() });
      await onRefresh();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(e.response?.data?.error || "Не удалось сохранить изменения");
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Удалить это фото безвозвратно?")) return;
    try {
      await api.delete(`/api/tickets/${ticket.id}/photos/${photoId}`);
      onRefresh();
    } catch (e) {
      alert("Не удалось удалить фото");
    }
  };

  const uploadPhotos = async (type, fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setUploadingType(type);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("photos", f));
      fd.append("photoType", type);
      await api.post(`/api/tickets/${ticket.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onRefresh();
    } catch (e) {
      alert("Не удалось загрузить фото");
    }
    setUploadingType(null);
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">⚙️ Полное редактирование — заявка #{ticket.id}</div>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: -6, marginBottom: 14, lineHeight: 1.4 }}>
          Доступно только суперадминистратору. Меняет статус на любой без ограничений обычного процесса — используйте аккуратно.
        </p>

        <div className="form-group">
          <label className="form-label">Статус заявки</label>
          <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
            {Object.entries(STATUS_LABELS_ALL).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Текст заявки</label>
          <textarea className="form-textarea" style={{ minHeight: 100 }} value={text} onChange={e => setText(e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Комментарий (необязательно)</label>
          <textarea className="form-textarea" style={{ minHeight: 50 }} value={comment} onChange={e => setComment(e.target.value)}
            placeholder="Например: причина ручного изменения..." />
        </div>

        {error && <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 10 }}>⚠ {error}</div>}

        <button className="btn btn-primary btn-sm" disabled={saving} onClick={save} style={{ width: "100%", marginBottom: 18 }}>
          {saving ? "Сохранение..." : "Сохранить изменения"}
        </button>

        <hr className="divider" />
        <div className="sec-label">Управление фото</div>

        {Object.entries(PHOTO_TYPE_LABELS).map(([type, label]) => {
          const photos = photosByType(type);
          return (
            <div key={type} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 6 }}>{label}</div>
              {photos.length > 0 && (
                <div className="photo-grid" style={{ marginBottom: 7 }}>
                  {photos.map(ph => (
                    <div key={ph.id} className="photo-thumb-wrap">
                      <img className="photo-thumb" src={`${photoBaseUrl}${ph.url}`} alt="" />
                      <button type="button" className="photo-remove-btn" title="Удалить фото"
                        onClick={() => deletePhoto(ph.id)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="upload-zone" onClick={() => fileRefs[type].current?.click()}>
                <input ref={fileRefs[type]} type="file" accept="image/*" multiple
                  onChange={e => { uploadPhotos(type, e.target.files); e.target.value = ""; }} />
                {uploadingType === type ? "Загрузка..." : `📎 Добавить фото (${label})`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailModal({ ticket, user, mechanics, onClose, onAssign, onUnassign, onEdit, onDelete, onToggleUrgent, onAccept, onReopen, onSoftDelete, onSendToParts, onMechanicAction, onPartsReady, onPartsAction, onApprovalAction, onManagerComment, onApprovalComment, onRefresh, onLightbox }) {
  const [assignTo, setAssignTo] = useState(ticket.assigned_to || mechanics[0]?.id || "");
  const [comment, setComment] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(ticket.text || "");
  const [editSaving, setEditSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [managerComment, setManagerComment] = useState(ticket.manager_comment || "");
  const [managerCommentSaved, setManagerCommentSaved] = useState(true);
  const [partsActionLoading, setPartsActionLoading] = useState(false);
  const [approvalComment, setApprovalComment] = useState(ticket.approval_comment || "");
  const [approvalCommentSaved, setApprovalCommentSaved] = useState(true);
  const [approvalActionLoading, setApprovalActionLoading] = useState(false);
  const [partsFiles, setPartsFiles] = useState([]);
  const [partsPreviews, setPartsPreviews] = useState([]);
  const [actFiles, setActFiles] = useState([]);
  const [actPreviews, setActPreviews] = useState([]);
  const [actError, setActError] = useState("");
  const [isCash, setIsCash] = useState(false);
  const [cashAmount, setCashAmount] = useState("");
  const [cashError, setCashError] = useState("");
  const [partsError, setPartsError] = useState("");
  const [actionError, setActionError] = useState("");
  const [reopenComment, setReopenComment] = useState("");
  const [reopenError, setReopenError] = useState("");
  const [reopenLoading, setReopenLoading] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [delError, setDelError] = useState("");
  const [delLoading, setDelLoading] = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [showSuperEdit, setShowSuperEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const partsFileRef = useRef();
  const actFileRef = useRef();
  const scFileRef = useRef();
  const [scFiles, setScFiles] = useState([]);
  const [scPreviews, setScPreviews] = useState([]);
  const [scComment, setScComment] = useState("");
  const [scError, setScError] = useState("");

  const sm = getStatusMeta(ticket);
  const isAdmin = user.role === "admin" || user.role === "superadmin";
  const isMechanic = user.role === "mechanic";
  const isParts = user.role === "parts";

  const canAssign   = isAdmin && (ticket.status === "unassigned" || ticket.status === "in_work" || ticket.status === "service_center");
  const canMechanic = isMechanic && ticket.status === "in_work";
  const PARTS_STAGE_STATUSES = ["awaiting_parts", "parts_clarification", "parts_ordered"];
  const APPROVAL_STAGE_STATUSES = ["approval", "approval_sent", "approval_approved"];
  const canParts    = isParts && (PARTS_STAGE_STATUSES.includes(ticket.status) || ticket.status === "service_center");
  const canApproval = (isParts || isAdmin) && APPROVAL_STAGE_STATUSES.includes(ticket.status);
  const canEditDelete = isAdmin && ticket.status === "unassigned";
  const canReopen = isAdmin && ticket.status === "closed";
  const isSuperadmin = user.role === "superadmin";

  const doSoftDelete = async () => {
    if (!delReason.trim()) {
      setDelError("Укажите причину удаления заявки");
      return;
    }
    if (!delConfirm) { setDelConfirm(true); return; }
    setDelError("");
    setDelLoading(true);
    try {
      await onSoftDelete(ticket.id, delReason.trim());
      onClose();
    } catch (e) {
      setDelLoading(false);
      setDelError(e.response?.data?.error || "Не удалось удалить заявку. Попробуйте ещё раз.");
    }
  };

  const doReopen = async (mode) => {
    if (!reopenComment.trim()) {
      setReopenError("Укажите причину переоткрытия заявки");
      return;
    }
    setReopenError("");
    setReopenLoading(true);
    try {
      await onReopen(ticket.id, mode, reopenComment.trim());
      onClose();
    } catch (e) {
      setReopenLoading(false);
      setReopenError(e.response?.data?.error || "Не удалось переоткрыть заявку. Попробуйте ещё раз.");
    }
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    setEditSaving(true);
    try {
      await onEdit(ticket.id, editText.trim());
      setEditMode(false);
    } finally { setEditSaving(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Удалить заявку #${ticket.id}? Это действие необратимо.`)) return;
    setDeleting(true);
    try {
      await onDelete(ticket.id);
      onClose();
    } finally { setDeleting(false); }
  };

  const handlePartsFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setPartsFiles(prev => [...prev, ...arr]);
    setPartsPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setPartsError("");
  };

  const removePartsFile = (idx) => {
    setPartsFiles(prev => prev.filter((_, i) => i !== idx));
    setPartsPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleActFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setActFiles(prev => [...prev, ...arr]);
    setActPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setActError("");
  };

  const removeActFile = (idx) => {
    setActFiles(prev => prev.filter((_, i) => i !== idx));
    setActPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleScFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setScFiles(prev => [...prev, ...arr]);
    setScPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
    setScError("");
  };

  const removeScFile = (idx) => {
    setScFiles(prev => prev.filter((_, i) => i !== idx));
    setScPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const doAction = async (action) => {
    setActionError("");

    // Наличка может быть отмечена на любом из трёх действий — проверяем сумму один раз
    let cashData = null;
    if (isCash && ["close", "parts", "service_center"].includes(action)) {
      const amountNum = Number(cashAmount);
      if (!cashAmount || isNaN(amountNum) || amountNum <= 0) {
        setCashError("Укажите корректную сумму");
        return;
      }
      cashData = { isCash: true, amount: amountNum };
    }
    setCashError("");

    if (action === "close") {
      if (actFiles.length === 0) {
        setActError("Приложите фото акта выполненных работ");
        return;
      }
      setLoading(true);
      try {
        await onMechanicAction(ticket.id, action, comment, actFiles, "act", cashData);
        onClose();
      } catch (e) {
        setLoading(false);
        setActionError(
          e.response?.data?.error ||
          "Не удалось загрузить фото или сохранить статус. Проверьте интернет-соединение и нажмите «Закрыть заявку» ещё раз — выбранные фото не потеряны."
        );
      }
      return;
    }
    if (action === "parts") {
      if (partsFiles.length === 0) {
        setPartsError("Приложите фото запчастей/АВР");
        return;
      }
      setLoading(true);
      try {
        await onMechanicAction(ticket.id, action, comment, partsFiles, "parts", cashData);
        onClose();
      } catch (e) {
        setLoading(false);
        setActionError(
          e.response?.data?.error ||
          "Не удалось загрузить фото или сохранить статус. Проверьте интернет-соединение и попробуйте ещё раз — выбранные фото не потеряны."
        );
      }
      return;
    }
    if (action === "service_center") {
      if (scFiles.length === 0) {
        setScError("Приложите фото для передачи в Сервис центр");
        return;
      }
      setLoading(true);
      try {
        await onMechanicAction(ticket.id, action, scComment, scFiles, "service_center", cashData);
        onClose();
      } catch (e) {
        setLoading(false);
        setActionError(
          e.response?.data?.error ||
          "Не удалось загрузить фото или сохранить статус. Проверьте интернет-соединение и попробуйте ещё раз — выбранные фото не потеряны."
        );
      }
      return;
    }
    setLoading(true);
    try {
      await onMechanicAction(ticket.id, action, comment, [], "parts");
      onClose();
    } catch (e) {
      setLoading(false);
      setActionError("Не удалось сохранить изменения. Проверьте интернет-соединение и попробуйте ещё раз.");
    }
  };

  const photoBaseUrl = window.location.origin;

  // Разбиваем фото по типам
  const ticketPhotos = (ticket.photos_typed || []).filter(p => p.type === "ticket");
  const partsPhotos  = (ticket.photos_typed || []).filter(p => p.type === "parts");
  const actPhotos    = (ticket.photos_typed || []).filter(p => p.type === "act");
  const scTypedPhotos = (ticket.photos_typed || []).filter(p => p.type === "service_center");
  // Обратная совместимость: если photos_typed нет, показываем все в старом порядке
  const legacyPhotos = ticket.photos_typed ? [] : (ticket.photos || []);

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <button className="modal-close" onClick={onClose}>✕</button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap", paddingRight: 34 }}>
          <div className="modal-title" style={{ margin: 0 }}>Заявка #{ticket.id}</div>
          <span className="badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
          {ticket.status === "in_work" && ticket.accepted_at && (
            <span className="badge-accepted" title={`Механик принял в работу: ${fmt(ticket.accepted_at)}`}>✅</span>
          )}
          {ticket.status === "in_work" && !ticket.accepted_at && isMechanic && ticket.assigned_to === user.id && (
            <button type="button" className="accept-btn" onClick={() => onAccept(ticket.id)}>✅ Принять в работу</button>
          )}
          {ticket.transferred && ticket.status === "unassigned" &&
            <span className="badge badge-transfer">Перенос</span>}
          {ticket.urgent && <span className="badge" style={{ background: "var(--red-bg)", color: "var(--red)" }}>❗ Срочно</span>}
          {ticket.reopened && <span className="badge" style={{ background: "#cffafe", color: "#0891b2" }}>♻ Переоткрыта</span>}
          {ticket.cash_total > 0 && <span className="badge" style={{ background: "#dcfce7", color: "#16a34a" }}>💵 {ticket.cash_total}₽</span>}
          {ticket.sent_to_service_center && <span className="badge" style={{ background: "#dbeafe", color: "#2563eb" }}>🏢 Сервис центр</span>}
          {ticket.is_maintenance && <span className="badge" style={{ background: "#fef9c3", color: "#a16207" }}>🔧 ТО</span>}
          {isAdmin && (
            <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
              {canEditDelete && !editMode && (<>
                <button className="btn-icon" title="Редактировать текст заявки"
                  onClick={() => { setEditText(ticket.text || ""); setEditMode(true); }}>✏️</button>
                <button className="btn-icon danger" title="Удалить заявку" disabled={deleting}
                  onClick={doDelete}>🗑</button>
              </>)}
              <button className={`urgent-toggle ${ticket.urgent ? "active" : ""}`}
                title={ticket.urgent ? "Снять пометку «Срочно»" : "Отметить как «Срочно»"}
                onClick={() => onToggleUrgent(ticket.id, !ticket.urgent)}>❗</button>
              {isSuperadmin && (
                <button className="btn-icon" title="Полное редактирование (суперадминистратор)"
                  onClick={() => setShowSuperEdit(true)}>⚙️</button>
              )}
            </div>
          )}
        </div>

        {showSuperEdit && (
          <SuperEditModal ticket={ticket} onClose={() => setShowSuperEdit(false)} onRefresh={onRefresh} />
        )}

        {ticket.assigned_name && (
          <div className="kv-row"><label>Механик</label><value>{ticket.assigned_name}</value></div>
        )}
        <div className="kv-row"><label>Создана</label><value>{fmt(ticket.created_at)}</value></div>

        {editMode ? (
          <div className="form-group" style={{ marginTop: 10 }}>
            <textarea className="form-textarea" style={{ minHeight: 120 }} value={editText}
              onChange={e => setEditText(e.target.value)} autoFocus />
            <div className="form-actions" style={{ marginTop: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setEditMode(false); setEditText(ticket.text || ""); }}>
                Отмена
              </button>
              <button className="btn btn-primary btn-sm" disabled={editSaving || !editText.trim()} onClick={saveEdit}>
                {editSaving ? "Сохраняем..." : "Сохранить"}
              </button>
            </div>
          </div>
        ) : (
          <div className="desc-box" style={{ marginTop: 10 }}>{linkifyPhones(ticket.text) || "—"}</div>
        )}

        {/* Фото заявки */}
        {ticketPhotos.length > 0 && <>
          <div className="sec-label">📎 Фото заявки</div>
          <div className="photo-grid">
            {ticketPhotos.map((p, i) => (
              p.url.endsWith(".pdf")
                ? <a key={i} href={`${photoBaseUrl}${p.url}`} target="_blank" rel="noreferrer"
                    className="pdf-link">📄 PDF документ</a>
                : <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                    onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
            ))}
          </div>
          <hr className="divider" />
        </>}

        {/* Фото запчастей + комментарии */}
        {(partsPhotos.length > 0 || ticket.parts_comments?.length > 0) && <>
          <div className="sec-label">📦 Запчасти</div>
          {partsPhotos.length > 0 && (
            <div className="photo-grid" style={{ marginBottom: 8 }}>
              {partsPhotos.map((p, i) => (
                <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                  onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
              ))}
            </div>
          )}
          {ticket.parts_comments?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {ticket.parts_comments.map((c, i) => (
                <div key={i} style={{ background: "#faf5ff", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#4c1d95", borderLeft: "3px solid #7c3aed" }}>
                  <div style={{ lineHeight: 1.4 }}>{c.text}</div>
                  <div style={{ fontSize: 11, color: "#7c3aed99", marginTop: 3 }}>{fmt(c.at)}</div>
                </div>
              ))}
            </div>
          )}
          <hr className="divider" />
        </>}

        {/* Комментарий менеджера запчастей (просмотр, для тех кто не может редактировать; скрыт от механика — конфиденциально) */}
        {!canParts && !isMechanic && ticket.manager_comment && (
          <>
            <div className="sec-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>🛠 Комментарий менеджера</span>
              <CopyButton text={ticket.manager_comment} />
            </div>
            <div style={{ background: "#ecfeff", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#164e63", borderLeft: "3px solid #0891b2", marginBottom: 12 }}>
              {ticket.manager_comment}
            </div>
            <hr className="divider" />
          </>
        )}

        {/* Комментарий администратора по согласованию (просмотр; скрыт от механика — конфиденциально) */}
        {!canApproval && !isMechanic && ticket.approval_comment && (
          <>
            <div className="sec-label">📝 Комментарий по согласованию</div>
            <div style={{ background: "#fef2f2", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#7f1d1d", borderLeft: "3px solid #dc2626", marginBottom: 12 }}>
              {ticket.approval_comment}
            </div>
            <hr className="divider" />
          </>
        )}

        {/* Фото АВР */}
        {actPhotos.length > 0 && <>
          <div className="sec-label">📋 Фото АВР / наклейка</div>
          <div className="photo-grid">
            {actPhotos.map((p, i) => (
              <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
            ))}
          </div>
          <hr className="divider" />
        </>}

        {scTypedPhotos.length > 0 && <>
          <div className="sec-label">🏢 Фото для Сервис центра</div>
          <div className="photo-grid">
            {scTypedPhotos.map((p, i) => (
              <img key={i} className="photo-thumb" src={`${photoBaseUrl}${p.url}`} alt=""
                onClick={() => onLightbox(`${photoBaseUrl}${p.url}`)} />
            ))}
          </div>
          <hr className="divider" />
        </>}

        {/* Обратная совместимость — старые фото без типа */}
        {legacyPhotos.length > 0 && <>
          <div className="sec-label">📷 Фото</div>
          <div className="photo-grid">
            {legacyPhotos.map((src, i) => (
              <img key={i} className="photo-thumb" src={`${photoBaseUrl}${src}`} alt=""
                onClick={() => onLightbox(`${photoBaseUrl}${src}`)} />
            ))}
          </div>
          <hr className="divider" />
        </>}

        {canAssign && (
          <div className="action-panel">
            <div className="action-panel-label">
              {ticket.status === "in_work" ? "Переназначить механика" : "Назначить механика"}
            </div>
            <div className="assign-row">
              <select className="form-select" value={assignTo} onChange={e => setAssignTo(e.target.value)}>
                {mechanics.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                await onAssign(ticket.id, assignTo); onClose();
              }}>
                {ticket.status === "in_work" ? "Переназначить" : "Назначить"}
              </button>
            </div>
            {ticket.status === "unassigned" && (
              <button className="btn btn-outline btn-sm" style={{ marginTop: 8, width: "100%" }}
                onClick={async () => { await onSendToParts(ticket.id); onClose(); }}>
                📦 Отправить сразу в «Ожидаем запчасти»
              </button>
            )}
            {ticket.status === "service_center" && (
              <button className="btn btn-outline btn-sm" style={{ marginTop: 8, width: "100%" }}
                onClick={async () => { await onUnassign(ticket.id); onClose(); }}>
                ↩ Вернуть в нераспределённые
              </button>
            )}
          </div>
        )}

        {canMechanic && (
          <div className="action-panel">
            <div className="action-panel-label">Действия</div>
            <div className="form-group">
              <label className="form-label">Комментарий</label>
              <textarea className="form-textarea" style={{ minHeight: 52 }} value={comment}
                onChange={e => setComment(e.target.value)} placeholder="Необязательно..." />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ color: partsFiles.length === 0 ? "var(--red)" : "var(--muted)" }}>
                📦 Фото запчастей/АВР <span style={{fontWeight:400,textTransform:"none"}}>(обязательно для «Ожидаем запчасти»)</span>
              </label>
              <div className="upload-zone" style={{ borderColor: partsError ? "var(--red)" : undefined }}
                onClick={() => partsFileRef.current?.click()}>
                <input ref={partsFileRef} type="file" accept="image/*" multiple
                  onChange={e => { handlePartsFiles(e.target.files); e.target.value = ""; }} />
                📎 Выбрать фото запчастей/АВР
              </div>
              {partsPreviews.length > 0 && (
                <div className="photo-grid" style={{ marginTop: 7 }}>
                  {partsPreviews.map((s, i) => (
                    <div key={i} className="photo-thumb-wrap">
                      <img className="photo-thumb" src={s} alt="" />
                      <button type="button" className="photo-remove-btn" title="Удалить фото"
                        onClick={() => removePartsFile(i)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              {partsError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {partsError}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" style={{ color: actFiles.length === 0 ? "var(--red)" : "var(--muted)" }}>
                📋 Фото АВР / наклейка <span style={{fontWeight:400,textTransform:"none"}}>(обязательно для закрытия)</span>
              </label>
              <div className="upload-zone" style={{ borderColor: actError ? "var(--red)" : undefined }}
                onClick={() => actFileRef.current?.click()}>
                <input ref={actFileRef} type="file" accept="image/*" multiple
                  onChange={e => { handleActFiles(e.target.files); e.target.value = ""; }} />
                📎 Выбрать фото АВР
              </div>
              {actPreviews.length > 0 && (
                <div className="photo-grid" style={{ marginTop: 7 }}>
                  {actPreviews.map((s, i) => (
                    <div key={i} className="photo-thumb-wrap">
                      <img className="photo-thumb" src={s} alt="" />
                      <button type="button" className="photo-remove-btn" title="Удалить фото"
                        onClick={() => removeActFile(i)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              {actError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {actError}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" style={{ color: scFiles.length === 0 ? "var(--red)" : "var(--muted)" }}>
                🏢 Фото для Сервис центра <span style={{fontWeight:400,textTransform:"none"}}>(обязательно для отправки)</span>
              </label>
              <div className="upload-zone" style={{ borderColor: scError ? "var(--red)" : undefined }}
                onClick={() => scFileRef.current?.click()}>
                <input ref={scFileRef} type="file" accept="image/*" multiple
                  onChange={e => { handleScFiles(e.target.files); e.target.value = ""; }} />
                📎 Выбрать фото
              </div>
              {scPreviews.length > 0 && (
                <div className="photo-grid" style={{ marginTop: 7 }}>
                  {scPreviews.map((s, i) => (
                    <div key={i} className="photo-thumb-wrap">
                      <img className="photo-thumb" src={s} alt="" />
                      <button type="button" className="photo-remove-btn" title="Удалить фото"
                        onClick={() => removeScFile(i)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              {scError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {scError}</div>}
              <textarea className="form-textarea" style={{ minHeight: 44, marginTop: 8 }} value={scComment}
                onChange={e => setScComment(e.target.value)} placeholder="Комментарий (необязательно)..." />
            </div>

            <div className="form-group">
              <button type="button" className={`cash-switch ${isCash ? "active" : ""}`}
                onClick={() => { setIsCash(v => !v); setCashError(""); }}>
                <span className="cash-switch-icon">💵</span>
                <span>Оплата наличными</span>
              </button>
              {isCash && (
                <div style={{ marginTop: 8 }}>
                  <input type="number" inputMode="decimal" className="form-input"
                    placeholder="Сумма, ₽" value={cashAmount}
                    onChange={e => { setCashAmount(e.target.value); setCashError(""); }}
                    style={{ borderColor: cashError ? "var(--red)" : undefined }} />
                  {cashError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {cashError}</div>}
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                    Сумма будет записана при нажатии любой из кнопок ниже — «Ожидаем запчасти», «Сервис центр» или «Закрыть»
                  </div>
                </div>
              )}
            </div>

            {actionError && (
              <div style={{
                background: "var(--red-bg)", color: "var(--red)", fontSize: 13, fontWeight: 600,
                padding: "10px 12px", borderRadius: 8, marginBottom: 10, lineHeight: 1.4,
              }}>
                ⚠ {actionError}
              </div>
            )}

            <div className="action-btns">
              <button className="btn btn-outline btn-sm" disabled={loading} onClick={() => doAction("parts")}>
                {loading ? "Отправка..." : "📦 Ожидаем запчасти"}
              </button>
              <button className="btn btn-ghost btn-sm" disabled={loading} onClick={() => doAction("transfer")}>
                🔄 Перенести
              </button>
              <button className="btn btn-outline btn-sm" disabled={loading}
                style={{ borderColor: "#2563eb", color: "#2563eb" }} onClick={() => doAction("service_center")}>
                {loading ? "Отправка..." : "🏢 Сервис центр"}
              </button>
              <button className="btn btn-red btn-sm" disabled={loading} onClick={() => doAction("close")}>
                {loading ? "Отправка..." : "✓ Закрыть"}
              </button>
            </div>
          </div>
        )}

        {canParts && (
          <div className="action-panel">
            <div className="action-panel-label">Менеджер запчастей</div>

            <div className="action-btns" style={{ marginBottom: 12 }}>
              <button className="btn btn-outline btn-sm" disabled={partsActionLoading}
                onClick={async () => { setPartsActionLoading(true); await onPartsAction(ticket.id, "clarification"); setPartsActionLoading(false); }}>
                ❓ Уточнение
              </button>
              <button className="btn btn-outline btn-sm" disabled={partsActionLoading}
                onClick={async () => { setPartsActionLoading(true); await onPartsAction(ticket.id, "ordered"); setPartsActionLoading(false); }}>
                🛒 Запчасти заказаны
              </button>
              <button className="btn btn-outline btn-sm" disabled={partsActionLoading}
                style={{ borderColor: "#dc2626", color: "#dc2626" }}
                onClick={async () => { setPartsActionLoading(true); await onPartsAction(ticket.id, "to_approval"); setPartsActionLoading(false); onClose(); }}>
                📨 Согласование
              </button>
              <button className="btn btn-green btn-sm" disabled={partsActionLoading}
                onClick={async () => { setPartsActionLoading(true); await onPartsReady(ticket.id); setPartsActionLoading(false); onClose(); }}>
                ✓ Запчасть готова
              </button>
              <button className="btn btn-ghost btn-sm" disabled={partsActionLoading}
                style={{ color: "#78716c" }}
                onClick={async () => {
                  if (!window.confirm("Списать заявку? Запчасти не найдены — заявка будет закрыта.")) return;
                  setPartsActionLoading(true); await onPartsAction(ticket.id, "write_off"); setPartsActionLoading(false); onClose();
                }}>
                🗑 Пшлнх
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Комментарий менеджера</label>
              <textarea className="form-textarea" style={{ minHeight: 70 }} value={managerComment}
                onChange={e => { setManagerComment(e.target.value); setManagerCommentSaved(false); }}
                placeholder="Заметки по запчастям, поставщику, срокам..." />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6, gap: 8, alignItems: "center" }}>
                {managerCommentSaved && managerComment && <span style={{ fontSize: 11, color: "var(--muted2)" }}>✓ сохранено</span>}
                <button className="btn btn-primary btn-sm" disabled={managerCommentSaved}
                  onClick={async () => { await onManagerComment(ticket.id, managerComment); setManagerCommentSaved(true); }}>
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        )}

        {canApproval && (
          <div className="action-panel">
            <div className="action-panel-label">Согласование</div>

            <div className="action-btns" style={{ marginBottom: 12 }}>
              {isAdmin && (
                <>
                  <button className="btn btn-outline btn-sm" disabled={approvalActionLoading}
                    style={{ borderColor: "#ca8a04", color: "#ca8a04" }}
                    onClick={async () => { setApprovalActionLoading(true); await onApprovalAction(ticket.id, "sent"); setApprovalActionLoading(false); }}>
                    📤 Отправлено на согласование
                  </button>
                  <button className="btn btn-outline btn-sm" disabled={approvalActionLoading}
                    style={{ borderColor: "#16a34a", color: "#16a34a" }}
                    onClick={async () => { setApprovalActionLoading(true); await onApprovalAction(ticket.id, "approved"); setApprovalActionLoading(false); }}>
                    ✅ Согласовано
                  </button>
                  <button className="btn btn-outline btn-sm" disabled={approvalActionLoading}
                    style={{ borderColor: "#dc2626", color: "#dc2626" }}
                    onClick={async () => {
                      if (!window.confirm("Отметить как «не согласовано»? Заявка будет закрыта.")) return;
                      setApprovalActionLoading(true); await onApprovalAction(ticket.id, "not_approved"); setApprovalActionLoading(false); onClose();
                    }}>
                    ❌ Не согласовано
                  </button>
                </>
              )}
              {isParts && (
                <button className="btn btn-green btn-sm" disabled={approvalActionLoading}
                  onClick={async () => { setApprovalActionLoading(true); await onApprovalAction(ticket.id, "ordered"); setApprovalActionLoading(false); onClose(); }}>
                  🛒 Запчасть заказана
                </button>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Комментарий администратора</label>
              {isAdmin ? (
                <>
                  <textarea className="form-textarea" style={{ minHeight: 70 }} value={approvalComment}
                    onChange={e => { setApprovalComment(e.target.value); setApprovalCommentSaved(false); }}
                    placeholder="Заметки по согласованию..." />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6, gap: 8, alignItems: "center" }}>
                    {approvalCommentSaved && approvalComment && <span style={{ fontSize: 11, color: "var(--muted2)" }}>✓ сохранено</span>}
                    <button className="btn btn-primary btn-sm" disabled={approvalCommentSaved}
                      onClick={async () => { await onApprovalComment(ticket.id, approvalComment); setApprovalCommentSaved(true); }}>
                      Сохранить
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ background: "#fef2f2", borderRadius: 7, padding: "8px 12px", fontSize: 13, color: "#7f1d1d", borderLeft: "3px solid #dc2626" }}>
                  {ticket.approval_comment || <span style={{ color: "#7f1d1d99" }}>Комментарий администратора отсутствует</span>}
                </div>
              )}
            </div>
          </div>
        )}

        {canReopen && (
          <div className="action-panel">
            <div className="action-panel-label">Переоткрыть заявку</div>
            <div className="form-group">
              <label className="form-label">Причина переоткрытия</label>
              <textarea className="form-textarea" style={{ minHeight: 70, borderColor: reopenError ? "var(--red)" : undefined }}
                value={reopenComment}
                onChange={e => { setReopenComment(e.target.value); setReopenError(""); }}
                placeholder="Например: клиент сообщил о повторной поломке..." />
              {reopenError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {reopenError}</div>}
            </div>
            <div className="action-btns">
              <button className="btn btn-outline btn-sm" disabled={reopenLoading} onClick={() => doReopen("new")}>
                🔄 Открыть заново
              </button>
              <button className="btn btn-outline btn-sm" disabled={reopenLoading}
                style={{ borderColor: "#0891b2", color: "#0891b2" }} onClick={() => doReopen("parts")}>
                📦 В «Ожидаем запчасти»
              </button>
            </div>
          </div>
        )}

        {isSuperadmin && (
          <div className="action-panel" style={{ borderColor: "var(--red)" }}>
            <div className="action-panel-label" style={{ color: "var(--red)" }}>Удаление заявки (суперадминистратор)</div>
            <p style={{ fontSize: 12, color: "var(--muted)", marginTop: -4, marginBottom: 10 }}>
              Заявка исчезнет из всех рабочих списков, но останется в «Журнале удалённых» в отчётах — ничего не теряется безвозвратно.
            </p>
            <div className="form-group">
              <label className="form-label">Причина удаления</label>
              <textarea className="form-textarea" style={{ minHeight: 60, borderColor: delError ? "var(--red)" : undefined }}
                value={delReason}
                onChange={e => { setDelReason(e.target.value); setDelError(""); setDelConfirm(false); }}
                placeholder="Например: дублирующая заявка, создана по ошибке..." />
              {delError && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 5 }}>⚠ {delError}</div>}
            </div>
            <button className="btn btn-sm" disabled={delLoading}
              style={{ background: delConfirm ? "var(--red)" : "transparent", color: delConfirm ? "#fff" : "var(--red)", border: "1.5px solid var(--red)" }}
              onClick={doSoftDelete}>
              {delLoading ? "Удаление..." : delConfirm ? "⚠ Нажмите ещё раз для подтверждения" : "🗑 Удалить заявку"}
            </button>
          </div>
        )}

        <hr className="divider" />
        <div className="sec-label">История</div>
        <div className="history">
          {(ticket.history || []).slice().reverse().map((h, i) => (
            <div key={i} className="h-item">
              <div className="h-dot" style={{ background: H_COLORS[h.type] || "#9ca3af" }} />
              <div className="h-text">{h.text}</div>
              <div className="h-time">{fmt(h.at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Reports Page ──────────────────────────────────────────────────────────────
const STATUS_LABELS_ALL = {
  unassigned:          "Нераспределена",
  in_work:             "В работе",
  awaiting_parts:      "Ожидаем запчасти",
  parts_clarification: "Уточнение запчастей",
  parts_ordered:       "Запчасти заказаны",
  approval:            "Согласование",
  approval_sent:       "Отправлено на согласование",
  approval_approved:   "Согласовано",
  closed:              "Закрыта",
  service_center:      "Сервис центр",
};

const CASH_ACTION_LABELS = {
  close: "Закрытие", parts: "Ожидаем запчасти", service_center: "Сервис центр",
};

function CashPage({ mechanics, user }) {
  const isAdminView = user.role === "admin" || user.role === "superadmin";
  const [mechFilter, setMechFilter] = useState("all");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/cash-payments");
      setPayments(res.data || []);
    } catch (e) {
      // тихо игнорируем — просто останется пусто
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = isAdminView && mechFilter !== "all"
    ? payments.filter(p => String(p.mechanic_id) === mechFilter)
    : payments;

  const groups = {};
  filtered.forEach(p => {
    const d = new Date(p.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    (groups[key] = groups[key] || []).push(p);
  });
  const monthKeys = Object.keys(groups).sort().reverse();
  const monthLabel = (key) => {
    const [y, m] = key.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
  };

  const totalAll = filtered.reduce((s, p) => s + Number(p.amount || 0), 0);
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const currentMonthTotal = (groups[currentMonthKey] || []).reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div className="content" style={{ maxWidth: 860 }}>
      <div className="page-hdr">
        <div className="page-hdr-title">💵 Наличка</div>
      </div>
      <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: -8, marginBottom: 14 }}>
        Каждая выплата учитывается в том месяце, когда она реально получена — независимо от того, закрыта ли заявка.
        По одной заявке может быть несколько выплат (например, часть при отправке в запчасти, остаток при закрытии).
      </p>

      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14, flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>Текущий месяц</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#16a34a" }}>{currentMonthTotal.toLocaleString("ru-RU")} ₽</div>
        </div>
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14, flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>Всего за всё время</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{totalAll.toLocaleString("ru-RU")} ₽</div>
        </div>
      </div>

      {isAdminView && (
        <div style={{ marginBottom: 14 }}>
          <select className="form-select" value={mechFilter} onChange={e => setMechFilter(e.target.value)}>
            <option value="all">Все механики</option>
            {mechanics.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
          </select>
        </div>
      )}

      {loading ? (
        <div style={{ color: "var(--muted)", padding: 30, textAlign: "center" }}>Загрузка...</div>
      ) : monthKeys.length === 0 ? (
        <div style={{ color: "var(--muted)", padding: 30, textAlign: "center" }}>Пока нет наличных выплат</div>
      ) : (
        monthKeys.map(key => {
          const items = groups[key].slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          const monthSum = items.reduce((s, p) => s + Number(p.amount || 0), 0);
          return (
            <div key={key} style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div className="sec-label" style={{ textTransform: "capitalize" }}>{monthLabel(key)}</div>
                <div style={{ fontWeight: 700, color: "#16a34a" }}>{monthSum.toLocaleString("ru-RU")} ₽</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {items.map(p => (
                  <div key={p.id} style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700 }}>#{p.ticket_id}</span>
                    <span style={{ flex: 1, fontSize: 13, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 100 }}>{p.ticket_text}</span>
                    <span style={{ fontSize: 11, color: "var(--muted)", background: "var(--bg)", padding: "2px 7px", borderRadius: 4 }}>
                      {CASH_ACTION_LABELS[p.action] || p.action}
                    </span>
                    {isAdminView && <span style={{ fontSize: 12, color: "var(--muted)" }}>👷 {p.mechanic_name || "—"}</span>}
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>{fmt(p.created_at)}</span>
                    <span style={{ fontWeight: 700, color: "#16a34a" }}>{Number(p.amount || 0).toLocaleString("ru-RU")} ₽</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function ReportsPage({ tickets, mechanics, user }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);

  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo]     = useState(today);
  const [mechFilter, setMechFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [format, setFormat] = useState("table"); // "table" | "cards"
  const [fields, setFields] = useState({
    id: true, date: true, text: true, mechanic: true,
    status: true, parts_comments: true, history: false,
  });
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [generating, setGenerating] = useState(false);

  const toggleField = (f) => setFields(prev => ({ ...prev, [f]: !prev[f] }));

  const filtered = tickets.filter(t => {
    const d = t.created_at?.slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo   && d > dateTo)   return false;
    if (mechFilter !== "all" && String(t.assigned_to) !== mechFilter) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    return true;
  });

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" })
      + " " + d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      let deletedTickets = [];
      if (includeDeleted && user?.role === "superadmin") {
        try {
          const res = await api.get("/api/tickets/deleted");
          deletedTickets = res.data || [];
        } catch (e) {
          console.error("Не удалось загрузить журнал удалённых заявок", e);
        }
      }

      const subtitle = [
        dateFrom && dateTo ? `Период: ${dateFrom} — ${dateTo}` : "",
        mechFilter !== "all" ? `Механик: ${mechanics.find(m => String(m.id) === mechFilter)?.name}` : "Все механики",
        statusFilter !== "all" ? `Статус: ${STATUS_LABELS_ALL[statusFilter]}` : "Все статусы",
        `Заявок: ${filtered.length}`,
        `Сформирован: ${fmtDate(new Date().toISOString())}`,
      ].filter(Boolean).join("  ·  ");

      let bodyHTML = "";

      if (format === "table") {
        const cols = [];
        if (fields.id)      cols.push(["№",         t => `#${t.id}`]);
        if (fields.date)    cols.push(["Дата",       t => fmtDate(t.created_at)]);
        if (fields.text)    cols.push(["Описание",   t => t.text || ""]);
        if (fields.mechanic) cols.push(["Механик",  t => t.assigned_name || "—"]);
        if (fields.status)  cols.push(["Статус",    t => STATUS_LABELS_ALL[t.status] || t.status]);
        if (fields.parts_comments) cols.push(["К запчастям", t => (t.parts_comments||[]).map(c=>c.text).join("; ") || "—"]);
        if (fields.history) cols.push(["История",   t => (t.history||[]).map(h=>`${fmtDate(h.at)}: ${h.text}`).join("<br/>")]);

        bodyHTML = `<table>
          <thead><tr>${cols.map(([h])=>`<th>${h}</th>`).join("")}</tr></thead>
          <tbody>${filtered.map((t,i)=>`<tr class="${i%2?"alt":""}">
            ${cols.map(([,fn])=>`<td>${fn(t)}</td>`).join("")}
          </tr>`).join("")}</tbody>
        </table>`;
      } else {
        bodyHTML = filtered.map(t => {
          const statusColor = { closed:"#6b7280", in_work:"#2563eb", awaiting_parts:"#7c3aed", unassigned:"#f59e0b", service_center:"#2563eb" };
          const sc = statusColor[t.status] || "#333";
          let card = `<div class="card">
            <div class="card-head">`;
          if (fields.id)      card += `<span class="card-id">#${t.id}</span>`;
          if (fields.date)    card += `<span class="card-date">${fmtDate(t.created_at)}</span>`;
          if (fields.mechanic && t.assigned_name) card += `<span class="card-mech">👷 ${t.assigned_name}</span>`;
          if (fields.status)  card += `<span class="card-status" style="color:${sc};border-color:${sc}">${STATUS_LABELS_ALL[t.status]}</span>`;
          card += `</div><div class="card-body">`;
          if (fields.text)    card += `<div class="card-text">${t.text || ""}</div>`;
          if (fields.parts_comments && t.parts_comments?.length) {
            card += `<div class="parts-block"><div class="parts-label">📦 К запчастям:</div>`;
            card += t.parts_comments.map(c=>`<div class="parts-item">— ${c.text}</div>`).join("");
            card += `</div>`;
          }
          if (fields.history && t.history?.length) {
            card += `<div class="history-block"><div class="history-label">ИСТОРИЯ</div>`;
            card += t.history.map(h=>`<div class="history-item"><span class="h-time">${fmtDate(h.at)}</span><span>${h.text}</span></div>`).join("");
            card += `</div>`;
          }
          card += `</div></div>`;
          return card;
        }).join("");
      }

      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
        <title>Отчёт по заявкам</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 20px; }
          h1 { font-size: 16px; margin-bottom: 4px; }
          .sub { font-size: 10px; color: #666; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f3f4f6; padding: 6px 8px; text-align: left; border-bottom: 2px solid #d1d5db; font-size: 11px; }
          td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; font-size: 11px; }
          tr.alt td { background: #f9fafb; }
          .card { border: 1px solid #e5e7eb; border-radius: 6px; margin-bottom: 10px; page-break-inside: avoid; overflow: hidden; }
          .card-head { background: #f3f4f6; padding: 6px 10px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
          .card-id { font-weight: bold; }
          .card-date { color: #666; }
          .card-status { font-size: 10px; font-weight: bold; border: 1.5px solid; border-radius: 4px; padding: 1px 6px; margin-left: auto; }
          .card-body { padding: 8px 10px; }
          .card-text { margin-bottom: 6px; line-height: 1.5; }
          .parts-block { background: #faf5ff; border-left: 3px solid #7c3aed; padding: 5px 8px; border-radius: 0 4px 4px 0; margin-bottom: 6px; }
          .parts-label { font-weight: bold; color: #7c3aed; font-size: 10px; margin-bottom: 2px; }
          .parts-item { font-size: 11px; }
          .history-block { border-top: 1px solid #e5e7eb; padding-top: 6px; margin-top: 4px; }
          .history-label { font-size: 9px; font-weight: bold; color: #999; text-transform: uppercase; margin-bottom: 3px; }
          .history-item { display: flex; gap: 8px; font-size: 10px; margin-bottom: 2px; }
          .h-time { color: #999; white-space: nowrap; min-width: 100px; }
          .deleted-section { margin-top: 24px; border-top: 2px solid #dc2626; padding-top: 10px; }
          .deleted-title { font-size: 13px; font-weight: bold; color: #dc2626; margin-bottom: 8px; }
          .deleted-item { border: 1px solid #fecaca; background: #fef2f2; border-radius: 5px; padding: 6px 10px; margin-bottom: 6px; font-size: 11px; }
          .deleted-item .d-id { font-weight: bold; margin-right: 8px; }
          .deleted-item .d-meta { color: #7f1d1d; font-size: 10px; margin-top: 2px; }
          .footer { margin-top: 16px; font-size: 10px; color: #999; border-top: 1px solid #e5e7eb; padding-top: 8px; }
          @media print { @page { margin: 15mm; } }
        </style>
      </head><body>
        <h1>Конвектомат Сервис — Отчёт по заявкам</h1>
        <div class="sub">${subtitle}</div>
        ${bodyHTML}
        ${deletedTickets.length ? `
        <div class="deleted-section">
          <div class="deleted-title">🗑 Журнал удалённых заявок (${deletedTickets.length})</div>
          ${deletedTickets.map(t => `
            <div class="deleted-item">
              <span class="d-id">#${t.id}</span>${(t.text || "").slice(0, 150)}
              <div class="d-meta">
                Удалена: ${fmtDate(t.deleted_at)} · Кем: ${t.deleted_by_name || "—"} ·
                Причина: ${t.delete_reason || "—"} · Статус на момент удаления: ${STATUS_LABELS_ALL[t.status] || t.status}
              </div>
            </div>
          `).join("")}
        </div>` : ""}
        <div class="footer">Итого заявок: ${filtered.length} · Закрытых: ${filtered.filter(t=>t.status==="closed").length} · В работе: ${filtered.filter(t=>t.status==="in_work").length}${deletedTickets.length ? ` · Удалённых: ${deletedTickets.length}` : ""}</div>
      </body></html>`;

      const win = window.open("", "_blank");
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
    } catch(e) {
      console.error(e);
      alert("Ошибка генерации PDF: " + e.message);
    }
    setGenerating(false);
  };

  const FIELD_LABELS = {
    id: "№ заявки", date: "Дата", text: "Описание", mechanic: "Механик",
    status: "Статус", parts_comments: "Комментарии к запчастям", history: "История изменений",
  };

  return (
    <div className="content" style={{ maxWidth: 860 }}>
      <div className="page-hdr">
        <div className="page-hdr-title">📊 Конструктор отчётов</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        {/* Период */}
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14 }}>
          <div className="sec-label" style={{ marginBottom: 10 }}>Период</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" className="form-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ flex: 1 }} />
            <span style={{ color: "var(--muted)", fontSize: 13 }}>—</span>
            <input type="date" className="form-input" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ flex: 1 }} />
          </div>
        </div>

        {/* Фильтры */}
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14 }}>
          <div className="sec-label" style={{ marginBottom: 10 }}>Фильтры</div>
          <div style={{ display: "flex", gap: 8 }}>
            <select className="form-select" value={mechFilter} onChange={e => setMechFilter(e.target.value)} style={{ flex: 1 }}>
              <option value="all">Все механики</option>
              {mechanics.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
            </select>
            <select className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ flex: 1 }}>
              <option value="all">Все статусы</option>
              <option value="unassigned">Нераспределённые</option>
              <option value="in_work">В работе</option>
              <option value="awaiting_parts">Ожидаем запчасти</option>
              <option value="parts_clarification">Уточнение запчастей</option>
              <option value="parts_ordered">Запчасти заказаны</option>
              <option value="approval">Согласование</option>
              <option value="approval_sent">Отправлено на согласование</option>
              <option value="approval_approved">Согласовано</option>
              <option value="closed">Закрытые</option>
              <option value="service_center">Сервис центр</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        {/* Поля */}
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14 }}>
          <div className="sec-label" style={{ marginBottom: 10 }}>Что включить</div>
          {Object.entries(FIELD_LABELS).map(([key, label]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={fields[key]} onChange={() => toggleField(key)}
                style={{ width: 15, height: 15, accentColor: "var(--blue)" }} />
              {label}
            </label>
          ))}
          {user?.role === "superadmin" && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, paddingTop: 10,
              borderTop: "1px dashed var(--border)", cursor: "pointer", fontSize: 13, color: "var(--red)" }}>
              <input type="checkbox" checked={includeDeleted} onChange={() => setIncludeDeleted(v => !v)}
                style={{ width: 15, height: 15, accentColor: "var(--red)" }} />
              🗑 Журнал удалённых заявок
            </label>
          )}
        </div>

        {/* Формат */}
        <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14 }}>
          <div className="sec-label" style={{ marginBottom: 10 }}>Формат</div>
          {[["table", "📋 Таблица", "Компактно, одна строка = одна заявка"], ["cards", "🗂 Карточки", "Развёрнуто, каждая заявка с деталями"]].map(([val, title, desc]) => (
            <label key={val} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12, cursor: "pointer",
              background: format === val ? "var(--blue-bg)" : "var(--bg)",
              border: `1.5px solid ${format === val ? "var(--blue)" : "var(--border)"}`,
              borderRadius: 8, padding: "10px 12px" }}>
              <input type="radio" name="format" value={val} checked={format === val} onChange={() => setFormat(val)}
                style={{ marginTop: 2, accentColor: "var(--blue)" }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{desc}</div>
              </div>
            </label>
          ))}

          <div style={{ marginTop: 12, padding: "10px 12px", background: "var(--bg)", borderRadius: 8, fontSize: 12, color: "var(--muted)" }}>
            Найдено заявок: <strong style={{ color: "var(--text)", fontSize: 14 }}>{filtered.length}</strong>
          </div>
        </div>
      </div>

      {/* Превью */}
      <div style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--radius)", padding: 14, marginBottom: 14 }}>
        <div className="sec-label" style={{ marginBottom: 10 }}>Превью (первые 3 заявки)</div>
        {filtered.length === 0 ? (
          <div className="empty" style={{ padding: "24px 0" }}>
            <div className="empty-icon">📭</div>
            <div className="empty-text">Нет заявок по выбранным фильтрам</div>
          </div>
        ) : format === "table" ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--bg)" }}>
                  {fields.id && <th style={thStyle}>№</th>}
                  {fields.date && <th style={thStyle}>Дата</th>}
                  {fields.text && <th style={thStyle}>Описание</th>}
                  {fields.mechanic && <th style={thStyle}>Механик</th>}
                  {fields.status && <th style={thStyle}>Статус</th>}
                  {fields.parts_comments && <th style={thStyle}>К запчастям</th>}
                  {fields.history && <th style={thStyle}>История</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 3).map((t, i) => (
                  <tr key={t.id} style={{ background: i % 2 ? "var(--bg)" : "var(--surface)" }}>
                    {fields.id && <td style={tdStyle}>#{t.id}</td>}
                    {fields.date && <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{fmtDate(t.created_at)}</td>}
                    {fields.text && <td style={tdStyle}>{(t.text || "").slice(0, 80)}{(t.text||"").length > 80 ? "…" : ""}</td>}
                    {fields.mechanic && <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{t.assigned_name || "—"}</td>}
                    {fields.status && <td style={tdStyle}>
                      <span style={{ background: (getStatusMeta(t).bg || "#eee"), color: (getStatusMeta(t).color || "#333"), padding: "2px 6px", borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {STATUS_LABELS_ALL[t.status]}
                      </span>
                    </td>}
                    {fields.parts_comments && <td style={tdStyle}>{(t.parts_comments || []).map(c => c.text).join("; ") || "—"}</td>}
                    {fields.history && <td style={tdStyle}>{(t.history || []).length} записей</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > 3 && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>... и ещё {filtered.length - 3} заявок в PDF</div>}
          </div>
        ) : (
          <div>
            {filtered.slice(0, 2).map(t => (
              <div key={t.id} style={{ border: "1px solid var(--border)", borderRadius: 8, marginBottom: 10, overflow: "hidden" }}>
                <div style={{ background: "var(--bg)", padding: "7px 12px", display: "flex", gap: 10, alignItems: "center", fontSize: 12 }}>
                  {fields.id && <strong>#{t.id}</strong>}
                  {fields.date && <span style={{ color: "var(--muted)" }}>{fmtDate(t.created_at)}</span>}
                  {fields.mechanic && t.assigned_name && <span>👷 {t.assigned_name}</span>}
                  {fields.status && <span style={{ marginLeft: "auto", background: getStatusMeta(t).bg, color: getStatusMeta(t).color, padding: "2px 7px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{getStatusMeta(t).label}</span>}
                </div>
                <div style={{ padding: "10px 12px", fontSize: 12 }}>
                  {fields.text && <div style={{ marginBottom: 6, lineHeight: 1.5 }}>{t.text}</div>}
                  {fields.parts_comments && t.parts_comments?.length > 0 && (
                    <div style={{ background: "#faf5ff", borderLeft: "3px solid #7c3aed", padding: "5px 10px", borderRadius: "0 5px 5px 0", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", marginBottom: 3 }}>📦 К запчастям:</div>
                      {t.parts_comments.map((c, i) => <div key={i} style={{ fontSize: 12 }}>— {c.text}</div>)}
                    </div>
                  )}
                  {fields.history && t.history?.length > 0 && (
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 6, marginTop: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", marginBottom: 4, textTransform: "uppercase" }}>История</div>
                      {(t.history || []).map((h, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, fontSize: 11, marginBottom: 2 }}>
                          <span style={{ color: "var(--muted2)", whiteSpace: "nowrap" }}>{fmtDate(h.at)}</span>
                          <span>{h.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {filtered.length > 2 && <div style={{ fontSize: 12, color: "var(--muted)" }}>... и ещё {filtered.length - 2} заявок в PDF</div>}
          </div>
        )}
      </div>

      <button className="btn btn-primary" disabled={generating || filtered.length === 0} onClick={generatePDF}
        style={{ width: "100%", justifyContent: "center", padding: "12px 0", fontSize: 14 }}>
        {generating ? "⏳ Формируем отчёт..." : `📥 Открыть отчёт PDF (${filtered.length} заявок)`}
      </button>
    </div>
  );
}

const thStyle = { padding: "7px 10px", textAlign: "left", borderBottom: "2px solid var(--border2)", fontWeight: 700, fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" };
const tdStyle = { padding: "6px 10px", borderBottom: "1px solid var(--border)", fontSize: 12, verticalAlign: "top" };
